import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Stack,
  Heading,
  Text,
  Badge,
  IconButton,
  Button,
  Divider,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Input,
  FormControl,
  FormLabel,
  Checkbox,
  useColorModeValue,
  useBreakpointValue,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  StatLabel,
  StatNumber,
  Grid,
  GridItem,
  Select as ChakraSelect,
  Tooltip,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Switch,
  Icon,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Alert,
  AlertIcon,
  Spinner,
  Center,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { SelectControl, TextareaControl } from 'formik-chakra-ui';
import {
  FiArrowLeft,
  FiPhone,
  FiMail,
  FiEdit2,
  FiTrash2,
  FiMoreVertical,
  FiExternalLink,
  FiSearch,
  FiCalendar,
  FiCheckCircle,
  FiMessageSquare,
  FiSave,
  FiPlus,
  FiArrowRight,
  FiZap,
  FiChevronDown,
  FiChevronUp,
  FiAlertTriangle,
  FiBriefcase,
} from 'react-icons/fi';
import { doc, onSnapshot, collection, query, where } from 'firebase/firestore';
import { db, functionsUsEast1 } from '../../utils/init-firebase';
import { httpsCallable } from 'firebase/functions';
import { supabase, isSupabaseConfigured, transformDealToCamelCase, transformContactToCamelCase } from '../../utils/supabase';
import { useVisibilityScope } from '../../hooks/useVisibilityScope';
import { applyVisibilityScope, buildVisibilityOrFilter, canUserSeeRecord, visibilityScopeKey } from '../../utils/recordVisibility';
import { buildDncUpdate } from '../../utils/dnc';
import { useAuth } from '../../contexts/AuthContext';
import { useHistory, useParams } from 'react-router-dom';
import { DatePickerField } from '../../components/DatePicker';
import {
  PROPERTY_TYPES,
  LOAN_PURPOSES,
  OCCUPANCY_TYPES,
  closedStagesLegacyPg,
  getNurtureRouteForSource,
} from '../../constants/crmConstants';
import { useCrmSettings } from '../../contexts/CrmSettingsContext';
import SubmissionDrawer from '../Loans/SubmissionDrawer';
import { useAgentPage } from '../../components/FutureBot/FutureBotProvider';
import { useWorkspaceAgentTools } from './Workspace/useWorkspaceAgentTools';
import UnifiedActivityTimeline from '../../components/CRM/UnifiedActivityTimeline';
import ArriveLinkModal from '../../components/CRM/ArriveLinkModal';
import TagSelector from '../../components/CRM/TagSelector';
import DialpadCallButton from '../../components/CRM/Dialpad/DialpadCallButton';
import DialpadSMSButton from '../../components/CRM/Dialpad/DialpadSMSButton';
import DncControl from '../../components/CRM/DncControl';
import CallHistoryPanel from '../../components/CRM/Dialpad/CallHistoryPanel';
import DealPartiesManager from '../../components/CRM/DealPartiesManager';
import ImportedDataCard from '../../components/CRM/ImportedDataCard';
import UnifiedEventModal from '../../components/CRM/Calendar/UnifiedEventModal';
import EmailTemplateModal from '../../components/CRM/Actions/EmailTemplateModal';
import SMSTemplateModal from '../../components/CRM/Actions/SMSTemplateModal';
import TaskDetailModal from '../../components/Tasks/TaskDetailModal';
import { useDealTasks } from '../../hooks/useDealTasks';
import { states } from '../../assets/allstates';
import moment from 'moment';
import TaskList from '../../components/Tasks/TaskList';
import LostDealReasonModal from '../../components/CRM/LostDealReasonModal';
import ContactCampaignPanel from '../../components/CRM/Campaigns/ContactCampaignPanel';
import MoveToNurtureModal from '../../components/CRM/Campaigns/MoveToNurtureModal';
import AriveSyncPanel from '../../components/CRM/Arive/AriveSyncPanel';
import DealQuickSearchModal from '../../components/CRM/DealQuickSearchModal';
import { useRecentDealViews } from '../../hooks/useRecentDealViews';
import { useTourActions } from '../../components/FeatureTour/FeatureTourProvider';
import AriveAutoSuggestBanner from '../../components/CRM/Arive/AriveAutoSuggestBanner';
import { groupCRMUsersByDTC } from '../../utils/crmUserSorting';
import { syncTagOwnershipOnReassignment } from '../../utils/tagOwnership';
import { getAriveDrift } from '../../utils/ariveDrift';

// FIXED: Module-level constants for empty objects to prevent new references
const EMPTY_AGENT = { name: '', email: '', phone: '', company: '' };
const EMPTY_ADDRESS = { street: '', city: '', state: '', zip: '', full: '' };

// "Next Lead" work-queue segment labels (button text per upcoming item)
const NEXT_LEAD_SEGMENT_LABELS = {
  'new-lead': 'Next Lead',
  'overdue': 'Next (Overdue)',
  'due-today': 'Next (Due Today)',
};

// ... (ValidationSchema remains the same) ...
const validationSchema = Yup.object().shape({
  stage: Yup.string().required('Status is required'),
  disposition: Yup.string(),
  nextAction: Yup.string(),
  nextActionDue: Yup.date().nullable(),
  notes: Yup.string().when('disposition', {
    is: (val) => val && val !== '',
    then: (schema) => schema.required('Notes are required when logging a disposition'),
    otherwise: (schema) => schema,
  }),
});


// Amber flag shown next to a field label when Origin disagrees with the last
// value synced from Arive. Loan financials are LOS-owned, so the user should
// treat Arive as the source of truth for these.
const AriveDriftBadge = ({ drift }) => {
  if (!drift) return null;
  return (
    <Tooltip
      hasArrow
      label={`Differs from Arive (Arive has: ${drift.ariveFormatted}). Loan data syncs from Arive — edit there to change it.`}
    >
      <Box as="span" display="inline-flex" verticalAlign="middle" ml={1.5}>
        <Icon as={FiAlertTriangle} color="orange.400" boxSize={3.5} aria-label="Differs from Arive" />
      </Box>
    </Tooltip>
  );
};

// Single-line text that shrinks its font size (down to minFontPx) instead of
// truncating when the available width is too small. Font size is applied
// imperatively — never via JSX — so unrelated re-renders can't reset it to the
// max size without the fitting effect re-running. Ellipsis only appears as a
// last resort once the minimum size is reached. `refitKey` forces a re-fit for
// layout changes the ResizeObserver can't see (e.g. a sibling badge changing
// width while this element's own box stays the same).
const ShrinkToFitText = ({ children, maxFontPx, minFontPx = 11, refitKey, ...textProps }) => {
  const textRef = useRef(null);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return undefined;

    const fit = () => {
      el.style.fontSize = `${maxFontPx}px`;
      if (!el.clientWidth) return; // hidden — nothing to measure
      let size = maxFontPx;
      let guard = 0;
      while (el.scrollWidth > el.clientWidth && size > minFontPx && guard < 40) {
        size = Math.max(minFontPx, size - 0.5);
        el.style.fontSize = `${size}px`;
        guard += 1;
      }
    };

    fit();

    // rAF defers the style writes out of the observer callback, avoiding
    // "ResizeObserver loop" warnings. Observing the parent catches the row
    // gaining/losing width (e.g. the Next Lead button mounting after the work
    // queue loads); observing the element itself catches siblings growing.
    const observer = new ResizeObserver(() => window.requestAnimationFrame(fit));
    observer.observe(el);
    if (el.parentElement) observer.observe(el.parentElement);
    return () => observer.disconnect();
  }, [children, maxFontPx, minFontPx, refitKey]);

  return (
    <Text ref={textRef} whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" minW={0} {...textProps}>
      {children}
    </Text>
  );
};

// V3.0: NEW COMPONENT for the sticky sidebar details
const DealDetailsCard = ({ contact, deal, isEditing, editedContact, setEditedContact, editedDeal, setEditedDeal, onToggleEdit }) => {
  const cardBG = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const ariveDrift = useMemo(() => getAriveDrift(deal), [deal]);

  return (
    <Box bg={cardBG} borderRadius="xl" borderWidth="1px" borderColor={borderColor} shadow="sm">
      <VStack align="stretch" spacing={0}>
        {/* Deal Vitals - All Editable */}
        <Box p={{ base: 4, md: 5 }}>
          <HStack justify="space-between" mb={4}>
            <Heading size="sm">Deal Vitals</Heading>
            {!isEditing && onToggleEdit && (
              <Tooltip label="Edit deal details">
                <IconButton
                  icon={<FiEdit2 />}
                  size="xs"
                  variant="ghost"
                  onClick={onToggleEdit}
                  aria-label="Edit"
                />
              </Tooltip>
            )}
          </HStack>
          <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={4}>
            {/* Loan Amount - Editable */}
            <FormControl minW={0}>
              <StatLabel fontSize="xs" mb={1}>Loan Amount<AriveDriftBadge drift={ariveDrift.amount} /></StatLabel>
              {isEditing ? (
                <Input
                  size="sm"
                  minW={0}
                  type="number"
                  value={editedDeal.amount}
                  onChange={(e) => setEditedDeal({ ...editedDeal, amount: e.target.value })}
                />
              ) : (
                <StatNumber fontSize="lg" color="green.600" noOfLines={1}>{deal.amount > 0 ? `$${deal.amount.toLocaleString()}` : '—'}</StatNumber>
              )}
            </FormControl>
            
            {/* Est. Closing - Editable */}
            <FormControl minW={0}>
              <StatLabel fontSize="xs" mb={1}>Est. Closing<AriveDriftBadge drift={ariveDrift.estimatedClosingDate} /></StatLabel>
              {isEditing ? (
                <Input
                  size="sm"
                  minW={0}
                  px={2}
                  type="date"
                  value={editedDeal.estimatedClosingDate ? moment(editedDeal.estimatedClosingDate.toDate ? editedDeal.estimatedClosingDate.toDate() : editedDeal.estimatedClosingDate).format('YYYY-MM-DD') : ''}
                  onChange={(e) => {
                    const [year, month, day] = e.target.value.split('-');
                    const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                    setEditedDeal({ ...editedDeal, estimatedClosingDate: localDate });
                  }}
                />
              ) : (
                <StatNumber fontSize="lg" noOfLines={1}>{deal.estimatedClosingDate ? moment(deal.estimatedClosingDate.toDate ? deal.estimatedClosingDate.toDate() : deal.estimatedClosingDate).format('MM/DD/YY') : '—'}</StatNumber>
              )}
            </FormControl>
            
            {/* Product - Editable */}
            <FormControl minW={0}>
              <StatLabel fontSize="xs" mb={1}>Product</StatLabel>
              {isEditing ? (
                <ChakraSelect
                  size="sm"
                  minW={0}
                  value={editedDeal.product}
                  onChange={(e) => setEditedDeal({ ...editedDeal, product: e.target.value })}
                >
                  <option value="">Select</option>
                  <option value="conventional">Conventional</option>
                  <option value="fha">FHA</option>
                  <option value="va">VA</option>
                  <option value="usda">USDA</option>
                  <option value="jumbo">Jumbo</option>
                  <option value="other">Other</option>
                </ChakraSelect>
              ) : (
                <StatNumber fontSize="md" noOfLines={1}>{deal.product || '—'}</StatNumber>
              )}
            </FormControl>
            
            {/* Source - Editable */}
            <FormControl minW={0}>
              <StatLabel fontSize="xs" mb={1}>Source</StatLabel>
              {isEditing ? (
                <ChakraSelect
                  size="sm"
                  minW={0}
                  value={editedDeal.source}
                  onChange={(e) => setEditedDeal({ ...editedDeal, source: e.target.value })}
                >
                  <option value="">Select</option>
                  <option value="google">Google</option>
                  <option value="aivo">AIVO</option>
                  <option value="portfolio">Past Client</option>
                  <option value="self-gen">Self-Generated</option>
                  <option value="organic">Organic</option>
                  <option value="spend">Spend</option>
                  <option value="referral">Referral</option>
                  <option value="zillow">Zillow</option>
                  <option value="realtor">Realtor.com</option>
                  <option value="arive">Arive</option>
                  <option value="other">Other</option>
                </ChakraSelect>
              ) : (
                <StatNumber fontSize="md" textTransform="capitalize" noOfLines={1}>{deal.source || '—'}</StatNumber>
              )}
            </FormControl>
            
            {/* Loan Number - Editable */}
            <FormControl minW={0}>
              <StatLabel fontSize="xs" mb={1}>Loan Number<AriveDriftBadge drift={ariveDrift.loanNumber} /></StatLabel>
              {isEditing ? (
                <Input
                  size="sm"
                  minW={0}
                  value={editedDeal.loanNumber}
                  onChange={(e) => setEditedDeal({ ...editedDeal, loanNumber: e.target.value })}
                  placeholder="Loan #"
                />
              ) : (
                <StatNumber fontSize="md" noOfLines={1}>{deal.loanNumber || '—'}</StatNumber>
              )}
            </FormControl>
            
            {/* Lender - Editable */}
            <FormControl minW={0}>
              <StatLabel fontSize="xs" mb={1}>Lender<AriveDriftBadge drift={ariveDrift.lender} /></StatLabel>
              {isEditing ? (
                <Input
                  size="sm"
                  minW={0}
                  value={editedDeal.lender}
                  onChange={(e) => setEditedDeal({ ...editedDeal, lender: e.target.value })}
                  placeholder="Lender Name"
                />
              ) : (
                <StatNumber fontSize="md" noOfLines={1}>{deal.lender || '—'}</StatNumber>
              )}
            </FormControl>
            
            {/* Arive ID - Editable */}
            <FormControl minW={0}>
              <StatLabel fontSize="xs" mb={1}>Arive ID</StatLabel>
              {isEditing ? (
                <Input
                  size="sm"
                  minW={0}
                  value={editedDeal.ariveId}
                  onChange={(e) => setEditedDeal({ ...editedDeal, ariveId: e.target.value })}
                  placeholder="Arive ID"
                />
              ) : (
                <StatNumber fontSize="md" noOfLines={1} wordBreak="break-all" color={deal.ariveId ? 'teal.600' : 'inherit'}>{deal.ariveId || '—'}</StatNumber>
              )}
            </FormControl>
          </Grid>
        </Box>
        <Divider />
        
        {/* Collapsible Details */}
        <Accordion defaultIndex={[0, 1, 2, 3]} allowMultiple>
          {/* Borrower Details */}
          <AccordionItem>
            <h2><AccordionButton><Box flex="1" textAlign="left" fontWeight="semibold">Borrower Details</Box><AccordionIcon /></AccordionButton></h2>
            <AccordionPanel pb={4} px={5}>
              <VStack spacing={3} align="stretch">
                {/* minW={0} on each control: a bare <input> has a ~20-character
                    intrinsic min-width that otherwise refuses to shrink in a flex row. */}
                <HStack align="flex-end" spacing={2}>
                  <FormControl minW={0}><FormLabel fontSize="sm">First Name</FormLabel><Input size="sm" minW={0} value={editedContact.firstName} onChange={(e) => setEditedContact({ ...editedContact, firstName: e.target.value })} isReadOnly={!isEditing} /></FormControl>
                  <FormControl minW={0} maxW={{ base: '56px', md: '80px' }}><FormLabel fontSize="sm">MI</FormLabel><Input size="sm" minW={0} px={2} value={editedContact.middleInitial} onChange={(e) => setEditedContact({ ...editedContact, middleInitial: e.target.value.toUpperCase() })} isReadOnly={!isEditing} /></FormControl>
                  <FormControl minW={0}><FormLabel fontSize="sm">Last Name</FormLabel><Input size="sm" minW={0} value={editedContact.lastName} onChange={(e) => setEditedContact({ ...editedContact, lastName: e.target.value })} isReadOnly={!isEditing} /></FormControl>
                </HStack>
                <FormControl minW={0}>
                  <FormLabel fontSize="sm">Email</FormLabel>
                  <Input
                    size="sm"
                    minW={0}
                    type="email"
                    value={editedContact.email || ''}
                    onChange={(e) => setEditedContact({ ...editedContact, email: e.target.value })}
                    isReadOnly={!isEditing}
                    placeholder="name@example.com"
                  />
                </FormControl>
                <FormControl minW={0}>
                  <FormLabel fontSize="sm">Phone</FormLabel>
                  <Input
                    size="sm"
                    minW={0}
                    type="tel"
                    value={editedContact.phone || ''}
                    onChange={(e) => setEditedContact({ ...editedContact, phone: e.target.value })}
                    isReadOnly={!isEditing}
                    placeholder="(555) 123-4567"
                  />
                </FormControl>
              </VStack>
            </AccordionPanel>
          </AccordionItem>
          
          {/* Co-Borrowers */}
          <AccordionItem>
            <h2>
              <AccordionButton>
                <Box flex="1" textAlign="left" fontWeight="semibold">
                  Co-Borrowers
                  {deal?.relatedParties?.filter(p => p.role === 'co-borrower').length > 0 && (
                    <Badge ml={2} colorScheme="blue">
                      {deal.relatedParties.filter(p => p.role === 'co-borrower').length}
                    </Badge>
                  )}
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4} px={5}>
              <VStack spacing={4} align="stretch">
                {(() => {
                  const coBorrowers = deal?.relatedParties?.filter(p => p.role === 'co-borrower') || [];
                  const legacyCoBorrowerData = {
                    firstName: contact?.coBorrowerFirstName,
                    lastName: contact?.coBorrowerLastName,
                    phone: contact?.coBorrowerPhone,
                    email: contact?.coBorrowerEmail,
                    name: contact?.coBorrowerName,
                  };
                  const hasLegacyData = legacyCoBorrowerData.firstName || legacyCoBorrowerData.name;

                  if (coBorrowers.length > 0) {
                    return (
                      <>
                        {coBorrowers.map((party, idx) => (
                          <Box key={idx} p={3} bg="blue.50" borderRadius="md">
                            <VStack align="flex-start" spacing={1}>
                              <Text fontWeight="semibold">{party.contactName}</Text>
                              {party.contactEmail && <Text fontSize="sm">{party.contactEmail}</Text>}
                              {party.contactPhone && <Text fontSize="sm">{party.contactPhone}</Text>}
                              {party.notes && (
                                <Text fontSize="xs" color="gray.600" fontStyle="italic">{party.notes}</Text>
                              )}
                            </VStack>
                          </Box>
                        ))}
                        <Text fontSize="xs" color="gray.500">
                          💡 Co-borrowers are managed via Related Parties section below
                        </Text>
                      </>
                    );
                  } else if (hasLegacyData) {
                    return (
                      <>
                        <Alert status="warning" size="sm">
                          <AlertIcon />
                          <VStack align="flex-start" spacing={1} flex={1}>
                            <Text fontSize="sm" fontWeight="semibold">Legacy Co-Borrower Data Found</Text>
                            <Text fontSize="xs">
                              {legacyCoBorrowerData.firstName 
                                ? `${legacyCoBorrowerData.firstName} ${legacyCoBorrowerData.lastName}`
                                : legacyCoBorrowerData.name}
                            </Text>
                            <Text fontSize="xs" color="gray.600">
                              Please add this co-borrower via Related Parties section for better data management
                            </Text>
                          </VStack>
                        </Alert>
                        
                        <Box p={3} bg="gray.50" borderRadius="md" borderWidth="1px" borderColor="gray.200">
                          <VStack align="flex-start" spacing={2}>
                            {legacyCoBorrowerData.firstName && (
                              <HStack>
                                <Text fontSize="xs" color="gray.600" minW="60px">Name:</Text>
                                <Text fontSize="sm">{legacyCoBorrowerData.firstName} {legacyCoBorrowerData.lastName}</Text>
                              </HStack>
                            )}
                            {legacyCoBorrowerData.name && (
                              <HStack>
                                <Text fontSize="xs" color="gray.600" minW="60px">Name (Legacy):</Text>
                                <Text fontSize="sm">{legacyCoBorrowerData.name}</Text>
                              </HStack>
                            )}
                            {legacyCoBorrowerData.phone && (
                              <HStack>
                                <Text fontSize="xs" color="gray.600" minW="60px">Phone:</Text>
                                <Text fontSize="sm">{legacyCoBorrowerData.phone}</Text>
                              </HStack>
                            )}
                            {legacyCoBorrowerData.email && (
                              <HStack>
                                <Text fontSize="xs" color="gray.600" minW="60px">Email:</Text>
                                <Text fontSize="sm">{legacyCoBorrowerData.email}</Text>
                              </HStack>
                            )}
                          </VStack>
                        </Box>
                      </>
                    );
                  } else {
                    return (
                      <Text fontSize="sm" color="gray.500">
                        No co-borrowers. Use Related Parties section below to add.
                      </Text>
                    );
                  }
                })()}
              </VStack>
            </AccordionPanel>
          </AccordionItem>

          {/* Property & Loan Details */}
          <AccordionItem>
            <h2><AccordionButton><Box flex="1" textAlign="left" fontWeight="semibold">Property & Loan</Box><AccordionIcon /></AccordionButton></h2>
            <AccordionPanel pb={4} px={5}>
              <VStack spacing={3} align="stretch">
                {/* Stacked on mobile: these option labels are too long for two columns.
                    spacing={2} matches the HStack default this replaced. */}
                <Stack direction={{ base: 'column', md: 'row' }} spacing={2}>
                  <FormControl minW={0}><FormLabel fontSize="sm">Loan Purpose</FormLabel><ChakraSelect size="sm" minW={0} value={editedDeal.loanPurpose} onChange={(e) => setEditedDeal({ ...editedDeal, loanPurpose: e.target.value })} isDisabled={!isEditing}>{LOAN_PURPOSES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}</ChakraSelect></FormControl>
                  <FormControl minW={0}><FormLabel fontSize="sm">Property Type</FormLabel><ChakraSelect size="sm" minW={0} value={editedDeal.propertyType} onChange={(e) => setEditedDeal({ ...editedDeal, propertyType: e.target.value })} isDisabled={!isEditing}>{PROPERTY_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}</ChakraSelect></FormControl>
                </Stack>
                <FormControl minW={0}>
                  <FormLabel fontSize="sm">Occupancy Type</FormLabel>
                  <ChakraSelect size="sm" minW={0} value={editedDeal.occupancyType || 'primary_residence'} onChange={(e) => setEditedDeal({ ...editedDeal, occupancyType: e.target.value })} isDisabled={!isEditing}>
                    {OCCUPANCY_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </ChakraSelect>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {OCCUPANCY_TYPES.find(o => o.value === (editedDeal.occupancyType || 'primary_residence'))?.description}
                  </Text>
                </FormControl>
                <Divider />
                <FormControl minW={0}><FormLabel fontSize="sm">Property Address<AriveDriftBadge drift={ariveDrift.propertyStreet} /></FormLabel><Input size="sm" minW={0} value={editedDeal.propertyAddress?.street} onChange={(e) => setEditedDeal({ ...editedDeal, propertyAddress: { ...editedDeal.propertyAddress, street: e.target.value } })} isReadOnly={!isEditing} /></FormControl>
                <HStack align="flex-end" spacing={2}>
                  <FormControl minW={0}><FormLabel fontSize="sm">City<AriveDriftBadge drift={ariveDrift.propertyCity} /></FormLabel><Input size="sm" minW={0} value={editedDeal.propertyAddress?.city} onChange={(e) => setEditedDeal({ ...editedDeal, propertyAddress: { ...editedDeal.propertyAddress, city: e.target.value } })} isReadOnly={!isEditing} /></FormControl>
                  <FormControl minW={0} maxW="70px"><FormLabel fontSize="sm">State<AriveDriftBadge drift={ariveDrift.propertyState} /></FormLabel><ChakraSelect size="sm" minW={0} px={1} value={editedDeal.propertyAddress?.state} onChange={(e) => setEditedDeal({ ...editedDeal, propertyAddress: { ...editedDeal.propertyAddress, state: e.target.value } })} isDisabled={!isEditing}>{states.map(s => <option key={s.code} value={s.code}>{s.code}</option>)}</ChakraSelect></FormControl>
                  <FormControl minW={0} maxW="80px"><FormLabel fontSize="sm">Zip<AriveDriftBadge drift={ariveDrift.propertyZip} /></FormLabel><Input size="sm" minW={0} px={2} value={editedDeal.propertyAddress?.zip} onChange={(e) => setEditedDeal({ ...editedDeal, propertyAddress: { ...editedDeal.propertyAddress, zip: e.target.value } })} isReadOnly={!isEditing} /></FormControl>
                </HStack>
              </VStack>
            </AccordionPanel>
          </AccordionItem>
          
          {/* Related Parties */}
          <AccordionItem>
            <h2><AccordionButton><Box flex="1" textAlign="left" fontWeight="semibold">Related Parties</Box><AccordionIcon /></AccordionButton></h2>
            <AccordionPanel pb={4} px={5}>
              <DealPartiesManager 
                deal={deal} 
                onPartyAdded={() => { /* toast handled in main */ }} 
                onPartyRemoved={() => { /* toast handled in main */ }} 
              />
            </AccordionPanel>
          </AccordionItem>

          {/* Imported Data — extra spreadsheet columns preserved at import time */}
          {deal?.extraFields && Object.keys(deal.extraFields).length > 0 && (
            <AccordionItem>
              <h2><AccordionButton><Box flex="1" textAlign="left" fontWeight="semibold">Imported Data</Box><AccordionIcon /></AccordionButton></h2>
              <AccordionPanel pb={4} px={5}>
                <Text fontSize="xs" color="gray.500" mb={3}>
                  Extra spreadsheet columns from the original import that don't have a standard Origin field.
                </Text>
                <ImportedDataCard extraFields={deal.extraFields} bare />
              </AccordionPanel>
            </AccordionItem>
          )}
        </Accordion>
      </VStack>
    </Box>
  );
};


export default function DealPage() {
  const { dealId } = useParams();
  const history = useHistory();
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const { dealStages, dispositions, nextActions, getStagesForPicker } = useCrmSettings();
  const toast = useToast();

  // Unique instance ID to prevent Supabase channel collisions on React re-mounts
  const instanceId = React.useRef(Math.random().toString(36).slice(2, 8));
  
  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');
  const isDTCLoanOfficer = Roles?.includes('DTC Loan Officer');

  // Multi-owner visibility: the viewer's effective deal book (owned + team
  // book-shares + record shares). Drives the Next Lead work queue, so shared
  // books surface their leads/tasks here too.
  const { scope: myDealScope, loading: myDealScopeLoading } = useVisibilityScope({
    entityType: 'deal',
    targetUserId: currentUser?.uid,
  });
  const myDealScopeKey = visibilityScopeKey(myDealScope) + ':g' + (myDealScope?.grantsVersion || 0);

  // Load CRM users for owner name resolution and task assignment
  const [crmUsers, setCrmUsers] = useState([]);
  useEffect(() => {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('CRMUser', '==', true));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const users = snapshot.docs.map(doc => ({
        uid: doc.id,
        name: doc.data().Name || doc.data().name || doc.data().email,
        ...doc.data()
      }));
      setCrmUsers(users);
    });
    return () => unsubscribe();
  }, []);

  // Use dealStages from context instead of hardcoded STAGES
  const STAGES = dealStages || [];

  // ... (All existing state remains the same) ...
  const [deal, setDeal] = useState(null);
  const [contact, setContact] = useState(null);

  const ownerName = deal?.ownerName || (deal?.ownerId ? crmUsers.find(u => u.uid === deal.ownerId)?.name : null) || 'Unassigned';

  // Detail-page visibility: URL knowledge must not grant access. Reactive so a
  // book share that resolves a moment later flips it open without a refetch.
  const canSeeDeal = useMemo(() => {
    if (!deal) return true; // nothing to protect yet (loading / not found)
    return canUserSeeRecord({
      uid: currentUser?.uid,
      isAdmin: isCRMAdmin,
      record: deal,
      accessibleOwnerIds: myDealScope?.ownerIds,
    });
  }, [deal, currentUser?.uid, isCRMAdmin, myDealScope]);
  const [activities, setActivities] = useState([]);
  // Bumped whenever activity data changes so UnifiedActivityTimeline refetches
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false); // V3.0: This now controls the sidebar
  const [isSaving, setIsSaving] = useState(false);
  const [editedContact, setEditedContact] = useState({});
  const [editedDeal, setEditedDeal] = useState({});
  const [dialpadEnabled, setDialpadEnabled] = useState(false);
  const [showTaskPrompt, setShowTaskPrompt] = useState(false);
  const [taskPromptData, setTaskPromptData] = useState(null);
  const [nurtureRouteData, setNurtureRouteData] = useState(null);

  // FutureBot: share this deal's live loan scenario (Quantum Matrix grounding).
  useAgentPage(
    useWorkspaceAgentTools({ contact, selectedDeal: deal, pageId: 'crm-deal-page', title: 'Deal Page' })
  );

  const charLimit = 32000;

  // Fetch ALL tasks for this deal directly by relatedTo.id
  // This ensures all tasks show regardless of who they're assigned to
  const { tasks: dealTasks, createTask, deleteTask, completeTask, reopenTask } = useDealTasks(dealId);
  const activeDealTasks = dealTasks.filter(task => task.status !== 'completed');
  
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isUnifiedEventOpen, onOpen: onUnifiedEventOpen, onClose: onUnifiedEventClose } = useDisclosure();
  const { isOpen: isTaskDetailOpen, onOpen: onTaskDetailOpen, onClose: onTaskDetailClose } = useDisclosure();
  const { isOpen: isSubmissionOpen, onOpen: onSubmissionOpen, onClose: onSubmissionClose } = useDisclosure();
  const { isOpen: isArriveOpen, onOpen: onArriveOpen, onClose: onArriveClose } = useDisclosure();
  const { isOpen: isEmailOpen, onOpen: onEmailOpen, onClose: onEmailClose } = useDisclosure();
  const { isOpen: isSMSOpen, onOpen: onSMSOpen, onClose: onSMSClose } = useDisclosure();
  const { isOpen: isLostReasonOpen, onOpen: onLostReasonOpen, onClose: onLostReasonClose } = useDisclosure();
  const { isOpen: isReassignOpen, onOpen: onReassignOpen, onClose: onReassignClose } = useDisclosure();
  const { isOpen: isNurtureOpen, onOpen: onNurtureOpen, onClose: onNurtureClose } = useDisclosure();
  // Quick-search: jump to another deal (or a recently viewed one) without leaving the page.
  const { isOpen: isQuickSearchOpen, onOpen: onQuickSearchOpen, onClose: onQuickSearchClose } = useDisclosure();
  // Mobile-only: collapses the contact/loan detail rows in the sticky header.
  const { isOpen: isMobileDetailsOpen, onToggle: onMobileDetailsToggle } = useDisclosure();
  const cancelRef = React.useRef();
  const [selectedTask, setSelectedTask] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [pendingLostStageUpdate, setPendingLostStageUpdate] = useState(null);
  const [isLostReasonSubmitting, setIsLostReasonSubmitting] = useState(false);
  // Priority work queue for the "Next Lead" button.
  // Each item: { id, segment: 'new-lead' | 'overdue' | 'due-today', earliestDue, dealName }
  const [workQueue, setWorkQueue] = useState([]);
  const [reassignUserId, setReassignUserId] = useState('');
  const [isReassigning, setIsReassigning] = useState(false);
  const [notifyNewOwner, setNotifyNewOwner] = useState(false);
  // Number of campaigns the deal's contact is actively enrolled in; reported
  // up by ContactCampaignPanel (which owns the fetch + realtime subscription)
  // so the Campaigns tab badge can show it without a duplicate query.
  const [activeCampaignCount, setActiveCampaignCount] = useState(0);

  // FeatureTour: tab-switch actions dispatch a REAL click on the tab header —
  // the exact same code path as a user click — instead of driving a controlled
  // index from outside React's event flow. Clicking also makes the conditional
  // Dialpad tab irrelevant (no index math) and can't go stale.
  useTourActions(useMemo(() => {
    const clickTab = (id) => document.querySelector(`[data-tour="${id}"]`)?.click();
    return {
      showActivityTab: () => clickTab('deal-tab-activity'),
      showTasksTab: () => clickTab('deal-tab-tasks'),
      showCampaignsTab: () => clickTab('deal-tab-campaigns'),
    };
  }, []));

  // --- Style Values ---
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const headerBG = useColorModeValue('white', 'gray.800');
  const cardBG = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400');
  // Max size for the auto-shrinking header name; matches the old md/lg tokens.
  const nameMaxFontPx = useBreakpointValue({ base: 16, md: 18 }) ?? 16;

  // Load deal from Supabase
  useEffect(() => {
    if (!dealId || !isSupabaseConfigured()) {
      setLoading(false);
      return;
    }

    const fetchDeal = async () => {
      const { data, error } = await supabase
        .from('crm_deals')
        .select('*')
        .eq('id', dealId)
        .single();

      if (error || !data) {
        console.error('Error loading deal:', error);
        toast({ title: 'Deal not found', status: 'error', duration: 5000 });
        history.push('/crm/my-deals');
        return;
      }

      setDeal(transformDealToCamelCase(data));
      setLoading(false);
    };

    fetchDeal();

    // Set up real-time subscription
    const channel = supabase
      .channel(`deal-${dealId}-${instanceId.current}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_deals', filter: `id=eq.${dealId}` }, (payload) => {
        if (payload.eventType === 'DELETE') {
          history.push('/crm/my-deals');
        } else {
          setDeal(transformDealToCamelCase(payload.new));
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [dealId, history, toast]);

  // Record this deal into the user's "recently viewed" history — only after a
  // successful load (deal.id matches the route param), so bad IDs never
  // pollute history. The hook dedupes, so realtime setDeal churn is harmless.
  const { recentViews, recordDealView } = useRecentDealViews();
  useEffect(() => {
    if (deal?.id && deal.id === dealId) {
      recordDealView(deal.id);
    }
  }, [deal?.id, dealId, recordDealView]);

  // Cmd/Ctrl+K opens the deal quick search (disabled while editing so a
  // stray shortcut can't navigate away from unsaved changes).
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (!isEditing) onQuickSearchOpen();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditing, onQuickSearchOpen]);

  // Load contact from Supabase
  useEffect(() => {
    if (!deal?.contactId || !isSupabaseConfigured()) return;

    const fetchContact = async () => {
      const { data, error } = await supabase
        .from('crm_contacts')
        .select('*')
        .eq('id', deal.contactId)
        .single();

      if (!error && data) {
        setContact(transformContactToCamelCase(data));
      }
    };

    fetchContact();

    // Set up real-time subscription
    const channel = supabase
      .channel(`contact-${deal.contactId}-${instanceId.current}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_contacts', filter: `id=eq.${deal.contactId}` }, (payload) => {
        if (payload.new) {
          setContact(transformContactToCamelCase(payload.new));
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [deal?.contactId]);

  // Load dialpad settings from Firestore (keeping in Firestore - user preferences)
  useEffect(() => {
    if (!currentUser?.uid) return;
    const userRef = doc(db, 'users', currentUser.uid);
    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        const userData = docSnap.data();
        // User must be both capable AND have enabled integration
        setDialpadEnabled(userData.dialpadCapable === true && userData.dialpadEnabled === true);
      }
    });
    return () => unsubscribe();
  }, [currentUser?.uid]);

  // Load activities from Supabase
  useEffect(() => {
    if (!dealId || !isSupabaseConfigured()) return;

    const fetchActivities = async () => {
      const { data, error } = await supabase
        .from('crm_activities')
        .select('*')
        .eq('deal_id', dealId)
        .order('created_at', { ascending: false });

      if (!error && data) {
        const transformed = data.map(activity => ({
          id: activity.id,
          text: activity.content,
          authorId: activity.author_id,
          timestamp: activity.created_at,
          activityType: activity.type,
          ...(activity.metadata || {}),
        }));
        setActivities(transformed);
      }
    };

    fetchActivities();

    // Set up real-time subscription for activities
    const channel = supabase
      .channel(`deal-activities-${dealId}-${instanceId.current}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_activities', filter: `deal_id=eq.${dealId}` }, () => {
        fetchActivities();
        setTimelineRefreshKey((k) => k + 1);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [dealId]);

  // Contact-level activity realtime (deal_id IS NULL rows — e.g. contact notes,
  // AI call summaries on deal-less calls) so the unified timeline stays live.
  useEffect(() => {
    if (!deal?.contactId || !isSupabaseConfigured()) return;

    const channel = supabase
      .channel(`deal-contact-activities-${deal.contactId}-${instanceId.current}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_activities', filter: `contact_id=eq.${deal.contactId}` }, () => {
        setTimelineRefreshKey((k) => k + 1);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [deal?.contactId]);

  // Build the prioritized "Next Lead" work queue:
  //   1. New Leads (stage 'lead-new', owned by me, newest first)
  //   2. Deals with an OVERDUE task (most overdue first)
  //   3. Deals with a task DUE TODAY (earliest first)
  // Task segments count tasks on deals I own OR tasks assigned to me, across
  // any non-closed deal (Lost/Funded/Won excluded).
  useEffect(() => {
    if (!currentUser?.uid || !isSupabaseConfigured()) return;

    let isMounted = true;

    const fetchWorkQueue = async () => {
      const uid = currentUser.uid;
      const todayEnd = moment().endOf('day').toISOString();

      // Segment 1: New Leads in my effective book (owned + shared), newest first
      let newLeadsQ = supabase
        .from('crm_deals')
        .select('id, created_at, deal_name')
        .eq('stage', 'lead-new')
        .order('created_at', { ascending: false });
      newLeadsQ = applyVisibilityScope(newLeadsQ, myDealScope);

      // Tasks on deals in my effective book (non-closed), due through end of
      // today. The visibility OR applies to the embedded crm_deals table.
      const ownedTasksQ = supabase
        .from('crm_tasks')
        .select('deal_id, due_date, crm_deals!inner(id, owner_id, stage, deal_name, created_at)')
        .in('status', ['pending', 'in-progress'])
        .not('due_date', 'is', null)
        .lte('due_date', todayEnd)
        .or(buildVisibilityOrFilter(myDealScope), { referencedTable: 'crm_deals' })
        .not('crm_deals.stage', 'in', closedStagesLegacyPg);

      // Tasks ASSIGNED to me (deal may be owned by others), non-closed
      const assignedTasksQ = supabase
        .from('crm_tasks')
        .select('deal_id, due_date, crm_deals!inner(id, owner_id, stage, deal_name, created_at)')
        .in('status', ['pending', 'in-progress'])
        .not('due_date', 'is', null)
        .lte('due_date', todayEnd)
        .eq('assigned_to', uid)
        .not('crm_deals.stage', 'in', closedStagesLegacyPg);

      const [newLeadsRes, ownedRes, assignedRes] = await Promise.all([
        newLeadsQ,
        ownedTasksQ,
        assignedTasksQ,
      ]);

      if (!isMounted) return;

      const newLeads = newLeadsRes.data || [];
      const newLeadIds = new Set(newLeads.map((d) => d.id));

      // Merge owned + assigned task rows, group by deal, track earliest due date.
      // A deal already in segment 1 (New Leads) is skipped so it never appears twice.
      const taskRows = [...(ownedRes.data || []), ...(assignedRes.data || [])];
      const dealMap = new Map(); // dealId -> { id, dealName, earliestDue }
      for (const row of taskRows) {
        const rowDealId = row.deal_id;
        const due = row.due_date;
        if (!rowDealId || !due || newLeadIds.has(rowDealId)) continue;
        const existing = dealMap.get(rowDealId);
        if (!existing) {
          dealMap.set(rowDealId, {
            id: rowDealId,
            dealName: row.crm_deals?.deal_name || 'Deal',
            earliestDue: due,
          });
        } else if (new Date(due) < new Date(existing.earliestDue)) {
          existing.earliestDue = due;
        }
      }

      // Classify each deal once by its earliest due date (all are <= end of today).
      const now = moment();
      const overdue = [];
      const dueToday = [];
      for (const dealItem of dealMap.values()) {
        if (moment(dealItem.earliestDue).isBefore(now, 'day')) {
          overdue.push(dealItem);
        } else {
          dueToday.push(dealItem);
        }
      }

      const byEarliestDue = (a, b) => new Date(a.earliestDue) - new Date(b.earliestDue);
      overdue.sort(byEarliestDue);
      dueToday.sort(byEarliestDue);

      const queue = [
        ...newLeads.map((d) => ({ id: d.id, segment: 'new-lead', earliestDue: null, dealName: d.deal_name })),
        ...overdue.map((d) => ({ id: d.id, segment: 'overdue', earliestDue: d.earliestDue, dealName: d.dealName })),
        ...dueToday.map((d) => ({ id: d.id, segment: 'due-today', earliestDue: d.earliestDue, dealName: d.dealName })),
      ];

      setWorkQueue(queue);
    };

    fetchWorkQueue();

    // Debounced refetch coalesces the double-fire when a single task change
    // triggers both the crm_tasks channel and (via the next_action_due trigger
    // bumping crm_deals.updated_at) the crm_deals channel.
    let debounceTimer = null;
    const scheduleRefetch = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        fetchWorkQueue();
      }, 300);
    };

    // Deals in my effective book: catches New-Lead stage changes + owned-deal
    // task changes (next_action_due trigger bumps updated_at on task
    // mutations). One filtered channel per accessible owner, capped.
    const watchedOwnerIds = (myDealScope?.ownerIds || [currentUser.uid]).slice(0, 10);
    const dealsChannels = watchedOwnerIds.map((watchedOwnerId) =>
      supabase
        .channel(`work-queue-deals-${watchedOwnerId}-${instanceId.current}`)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'crm_deals',
          filter: `owner_id=eq.${watchedOwnerId}`,
        }, scheduleRefetch)
        .subscribe()
    );

    // All task changes: catches tasks assigned to me on deals owned by others.
    const tasksChannel = supabase
      .channel(`work-queue-tasks-${currentUser.uid}-${instanceId.current}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'crm_tasks',
      }, scheduleRefetch)
      .subscribe();

    return () => {
      isMounted = false;
      if (debounceTimer) clearTimeout(debounceTimer);
      dealsChannels.forEach((ch) => supabase.removeChannel(ch));
      supabase.removeChannel(tasksChannel);
    };
    // myDealScopeKey covers myDealScope content (incl. grant versions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, myDealScopeKey]);

  
  // FIXED: useEffect to initialize editable fields with stable object references
  useEffect(() => {
    if (contact) {
      setEditedContact({
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        email: contact.email || '',
        phone: contact.phone || '',
        middleInitial: contact.middleInitial || '',
        // Co-borrower fields removed - now managed via Related Parties
      });
    }
    if (deal) {
      setEditedDeal({
        amount: deal.amount || 0,
        product: deal.product || '',
        source: deal.source || '',
        estimatedClosingDate: deal.estimatedClosingDate || null,
        tags: deal.tags || [],
        ariveId: deal.ariveId || '',
        loanNumber: deal.loanNumber || '',
        lender: deal.lender || '',
        buyerAgent: deal.buyerAgent || EMPTY_AGENT,
        sellerAgent: deal.sellerAgent || EMPTY_AGENT,
        propertyAddress: deal.propertyAddress || EMPTY_ADDRESS,
        propertyType: deal.propertyType || '',
        occupancyType: deal.occupancyType || 'primary_residence',
        purchaseOrRefi: deal.purchaseOrRefi || '',
        loanPurpose: deal.loanPurpose || '',
        downPayment: deal.downPayment || '',
        creditScore: deal.creditScore || '',
        appDate: deal.appDate || null,
      });
    }
  }, [contact, deal]);

  // --- Handlers ---

  const handleReassignDeal = async () => {
    if (!reassignUserId || !isSupabaseConfigured()) return;

    setIsReassigning(true);
    try {
      const previousOwnerId = deal.ownerId || null;
      const assignedUser = crmUsers.find(u => u.uid === reassignUserId);
      const newOwnerName = assignedUser?.name || 'Unknown';

      const { error: dealError } = await supabase
        .from('crm_deals')
        .update({ owner_id: reassignUserId })
        .eq('id', dealId);
      if (dealError) throw dealError;

      if (deal.contactId) {
        const { error: contactError } = await supabase
          .from('crm_contacts')
          .update({ owner_id: reassignUserId })
          .eq('id', deal.contactId);
        if (contactError) throw contactError;
      }

      const { data: newActivity, error: activityError } = await supabase
        .from('crm_activities')
        .insert({
          deal_id: dealId,
          contact_id: deal.contactId || null,
          author_id: currentUser.uid,
          type: 'transfer',
          content: `Deal reassigned to ${newOwnerName}`,
          metadata: {
            authorName: currentUser.displayName || currentUser.email,
            authorPhotoURL: currentUser.photoURL || '',
            previousOwnerId,
            newOwnerId: reassignUserId,
          },
        })
        .select()
        .single();
      if (activityError) throw activityError;

      await supabase.from('crm_audit_logs').insert({
        action: 'deal_reassignment',
        user_id: currentUser.uid,
        user_name: currentUser.displayName || currentUser.email,
        entity_type: 'deal',
        details: {
          dealId,
          dealName: deal.dealName,
          previousOwnerId,
          newOwnerId: reassignUserId,
          newOwnerName,
        },
      });

      await syncTagOwnershipOnReassignment('deal', [dealId], reassignUserId);
      if (deal.contactId) {
        await syncTagOwnershipOnReassignment('contact', [deal.contactId], reassignUserId);
      }

      // Notify the new owner only when opted in (best-effort; never blocks the reassign flow).
      if (notifyNewOwner && reassignUserId !== currentUser.uid) {
        const assigneeEmail = assignedUser?.Email || assignedUser?.email || '';

        try {
          await httpsCallable(functionsUsEast1, 'notifyLoanOfficerSMS')({
            dealId,
            assigneeId: reassignUserId,
            assignmentType: 'reassignment',
          });
        } catch (smsErr) {
          console.error('LO reassignment SMS failed (non-blocking):', smsErr);
        }

        if (assigneeEmail) {
          try {
            await httpsCallable(functionsUsEast1, 'sendLeadAssignmentNotification')({
              dealId,
              assigneeId: reassignUserId,
              assigneeName: newOwnerName,
              assigneeEmail,
            });
          } catch (emailErr) {
            console.error('LO reassignment email failed (non-blocking):', emailErr);
          }
        }
      }

      setDeal(prev => ({ ...prev, ownerId: reassignUserId, ownerName: newOwnerName }));

      if (newActivity) {
        const transformedActivity = {
          id: newActivity.id,
          text: newActivity.content,
          authorId: newActivity.author_id,
          timestamp: newActivity.created_at,
          activityType: newActivity.type,
          ...(newActivity.metadata || {}),
        };
        setActivities(prev => [transformedActivity, ...prev]);
        setTimelineRefreshKey((k) => k + 1);
      }

      toast({ title: 'Deal reassigned', description: `Reassigned to ${newOwnerName}`, status: 'success', duration: 3000 });
      setReassignUserId('');
      setNotifyNewOwner(false);
      onReassignClose();
    } catch (error) {
      console.error('Error reassigning deal:', error);
      toast({ title: 'Error reassigning deal', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setIsReassigning(false);
    }
  };

  // Admin-only: flip this deal between a personal record and a company record
  // (record_scope='company' → visible to every CRM admin via the visibility
  // predicate). _updated_by='import' suppresses the deal.updated audit event —
  // scope flips are administrative bookkeeping and must never fire automations.
  const handleToggleCompanyScope = async () => {
    if (!isCRMAdmin || !deal?.id) return;

    const previousScope = deal.recordScope || 'user';
    const newScope = previousScope === 'company' ? 'user' : 'company';

    setDeal(prev => ({ ...prev, recordScope: newScope }));

    try {
      const { error } = await supabase
        .from('crm_deals')
        .update({ record_scope: newScope, _updated_by: 'import' })
        .eq('id', deal.id);
      if (error) throw error;

      await supabase.from('crm_audit_logs').insert({
        action: newScope === 'company' ? 'record_scope_company' : 'record_scope_personal',
        user_id: currentUser.uid,
        user_name: currentUser.displayName || currentUser.email,
        entity_type: 'deal',
        details: { dealId: deal.id, dealName: deal.dealName, previousScope, newScope },
      });

      toast({
        title: newScope === 'company' ? 'Marked as company record' : 'Converted to personal record',
        description: newScope === 'company'
          ? 'All CRM admins can now see this deal.'
          : 'Visibility is back to the owner (plus any shares).',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error toggling record scope:', error);
      setDeal(prev => ({ ...prev, recordScope: previousScope }));
      toast({ title: 'Error updating record scope', description: error.message, status: 'error', duration: 4000 });
    }
  };

  const currentQueueIndex = workQueue.findIndex((item) => item.id === dealId);

  // Index/item the "Next" button will navigate to (wrap-around, matches prior behavior).
  const nextQueueIndex = workQueue.length === 0
    ? -1
    : currentQueueIndex === -1
      ? 0
      : (currentQueueIndex + 1) % workQueue.length;
  const nextQueueItem = nextQueueIndex >= 0 ? workQueue[nextQueueIndex] : null;

  // Per-segment counts for the button tooltip breakdown.
  const queueSegmentCounts = workQueue.reduce(
    (acc, item) => {
      acc[item.segment] = (acc[item.segment] || 0) + 1;
      return acc;
    },
    { 'new-lead': 0, 'overdue': 0, 'due-today': 0 }
  );

  const nextLeadButtonLabel = nextQueueItem
    ? (NEXT_LEAD_SEGMENT_LABELS[nextQueueItem.segment] || 'Next Lead')
    : 'Next Lead';
  const nextLeadTooltipLabel = (workQueue.length === 1 && currentQueueIndex === 0)
    ? 'Nothing else in your queue'
    : `New Leads: ${queueSegmentCounts['new-lead']} · Overdue: ${queueSegmentCounts['overdue']} · Due Today: ${queueSegmentCounts['due-today']}`;

  const handleNextLead = () => {
    if (workQueue.length === 0) return;
    const nextIndex = currentQueueIndex === -1
      ? 0
      : (currentQueueIndex + 1) % workQueue.length;
    history.push(`/crm/deals/${workQueue[nextIndex].id}`);
  };

  // Handler for Lost Deal reason confirmation (DTC Loan Officers)
  const handleLostReasonConfirm = async ({ reason, reasonLabel, details }) => {
    if (!pendingLostStageUpdate) return;
    
    setIsLostReasonSubmitting(true);
    try {
      // Call Cloud Function to send lost deal notification email
      const sendLostDealNotification = httpsCallable(functionsUsEast1, 'sendLostDealNotification');
      await sendLostDealNotification({
        dealId,
        reason,
        reasonDetails: details,
        loanOfficerName: currentUser.displayName || currentUser.email,
        loanOfficerEmail: currentUser.email,
      });
      
      // Proceed with the original stage update
      await performStageUpdate(pendingLostStageUpdate, { 
        resetForm: () => {},
        lostReason: reason,
        lostReasonLabel: reasonLabel,
        lostReasonDetails: details,
      });
      
      toast({ 
        title: 'Deal marked as Lost', 
        description: 'Notification sent to management',
        status: 'success', 
        duration: 3000 
      });
      
      onLostReasonClose();
      setPendingLostStageUpdate(null);
    } catch (error) {
      console.error('Error processing lost deal:', error);
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to process lost deal notification',
        status: 'error', 
        duration: 5000 
      });
    } finally {
      setIsLostReasonSubmitting(false);
    }
  };

  // Core function to perform stage update (extracted for reuse)
  const performStageUpdate = async (values, actions) => {
    const changes = [];
    if (values.stage !== deal.stage) changes.push(`Stage changed: ${deal.stage} → ${values.stage}`);
    if (values.nextAction !== deal.nextAction) changes.push(`Next action: ${values.nextAction}`);
    if (values.nextActionDue) changes.push(`Due date: ${moment(values.nextActionDue).format('MMM DD, YYYY')}`);
    
    // Add lost reason to changes if provided
    if (actions.lostReasonLabel) {
      changes.push(`Lost Reason: ${actions.lostReasonLabel}`);
    }

    // Update deal in Supabase
    const { error: dealError } = await supabase
      .from('crm_deals')
      .update({
        stage: values.stage,
        next_action: values.nextAction || '',
        next_action_due: values.nextActionDue ? new Date(values.nextActionDue).toISOString() : null,
        is_at_risk: false,
      })
      .eq('id', dealId);

    if (dealError) throw dealError;
    
    // Mark contact as DNC (Everything) if disposition is do-not-contact
    if (values.disposition === 'do-not-contact' && deal.contactId) {
      await supabase
        .from('crm_contacts')
        .update(buildDncUpdate({}, { everything: true }))
        .eq('id', deal.contactId);
      changes.push('⚠️ Marked as Do Not Contact');
    }

    // Build activity content with lost reason details if provided
    let activityContent = values.notes || 'Deal updated';
    if (actions.lostReasonDetails && values.stage === 'deal-lost') {
      activityContent = `🚫 Deal marked as Lost\n\nReason: ${actions.lostReasonLabel}\n\nDetails: "${actions.lostReasonDetails}"${values.notes ? `\n\nNotes: ${values.notes}` : ''}`;
    }

    // Add activity to Supabase (stamped with both deal and contact so it
    // surfaces on contact-level notes views too)
    const activityData = {
      deal_id: dealId,
      contact_id: deal.contactId || null,
      author_id: currentUser.uid,
      type: values.disposition ? 'disposition' : (values.stage === 'deal-lost' ? 'stage_change' : 'update'),
      content: activityContent,
      metadata: {
        authorName: currentUser.displayName || currentUser.email,
        authorPhotoURL: currentUser.photoURL || '',
        disposition: values.disposition || '',
        changes,
        previousStage: deal.stage,
        newStage: values.stage,
        ...(actions.lostReason && {
          lostReason: actions.lostReason,
          lostReasonLabel: actions.lostReasonLabel,
          lostReasonDetails: actions.lostReasonDetails,
        }),
      },
    };

    const { data: newActivity, error: activityError } = await supabase
      .from('crm_activities')
      .insert(activityData)
      .select()
      .single();

    if (activityError) throw activityError;

    try {
      const fireTrigger = httpsCallable(functionsUsEast1, 'handleAutomationTriggers');
      if (values.stage !== deal.stage) {
        fireTrigger({ eventType: 'deal.stage_changed', payload: { contactId: deal.contactId, dealId, fromStage: deal.stage, toStage: values.stage } });
      }
      if (values.disposition) {
        fireTrigger({ eventType: 'activity.disposition_logged', payload: { contactId: deal.contactId, dealId, disposition: values.disposition } });
      }
    } catch (_) {}

    // INSTANT UPDATE: Update deal state immediately
    setDeal(prev => ({
      ...prev,
      stage: values.stage,
      nextAction: values.nextAction || '',
      nextActionDue: values.nextActionDue || null,
      isAtRisk: false,
    }));

    // INSTANT UPDATE: Add new activity to timeline immediately
    if (newActivity) {
      const transformedActivity = {
        id: newActivity.id,
        text: newActivity.content,
        authorId: newActivity.author_id,
        timestamp: newActivity.created_at,
        activityType: newActivity.type,
        ...(newActivity.metadata || {}),
      };
      setActivities(prev => [transformedActivity, ...prev]);
      setTimelineRefreshKey((k) => k + 1);
    }
    
    if (values.nextAction && values.nextActionDue) {
      setTaskPromptData({ nextAction: values.nextAction, nextActionDue: values.nextActionDue, dealName: deal.dealName });
      setShowTaskPrompt(true);
    }
  };

  // handleSubmit for Activity Log - uses Supabase
  const handleSubmit = async (values, actions) => {
    if (!isSupabaseConfigured()) {
      toast({ title: 'Database not configured', status: 'error', duration: 5000 });
      return;
    }

    // DTC Loan Officer Lost Deal Workflow - intercept stage change to Lost
    if (values.stage === 'deal-lost' && deal.stage !== 'deal-lost' && isDTCLoanOfficer) {
      setPendingLostStageUpdate(values);
      onLostReasonOpen();
      return; // Don't proceed until modal is completed
    }

    setIsSubmitting(true);
    try {
      const changes = [];
      if (values.stage !== deal.stage) changes.push(`Stage changed: ${deal.stage} → ${values.stage}`);
      if (values.nextAction !== deal.nextAction) changes.push(`Next action: ${values.nextAction}`);
      if (values.nextActionDue) changes.push(`Due date: ${moment(values.nextActionDue).format('MMM DD, YYYY')}`);

      // Update deal in Supabase
      const { error: dealError } = await supabase
        .from('crm_deals')
        .update({
          stage: values.stage,
          next_action: values.nextAction || '',
          next_action_due: values.nextActionDue ? new Date(values.nextActionDue).toISOString() : null,
          is_at_risk: false,
        })
        .eq('id', dealId);

      if (dealError) throw dealError;
      
      // Mark contact as DNC (Everything) if disposition is do-not-contact
      if (values.disposition === 'do-not-contact' && deal.contactId) {
        await supabase
          .from('crm_contacts')
          .update(buildDncUpdate({}, { everything: true }))
          .eq('id', deal.contactId);
        changes.push('⚠️ Marked as Do Not Contact');
      }

      // Add activity to Supabase (stamped with both deal and contact so it
      // surfaces on contact-level notes views too)
      const activityData = {
        deal_id: dealId,
        contact_id: deal.contactId || null,
        author_id: currentUser.uid,
        type: values.disposition ? 'disposition' : 'update',
        content: values.notes || 'Deal updated',
        metadata: {
          authorName: currentUser.displayName || currentUser.email,
          authorPhotoURL: currentUser.photoURL || '',
          disposition: values.disposition || '',
          changes,
          previousStage: deal.stage,
          newStage: values.stage,
        },
      };

      const { data: newActivity, error: activityError } = await supabase
        .from('crm_activities')
        .insert(activityData)
        .select()
        .single();

      if (activityError) throw activityError;

      // Fire automation triggers (fire-and-forget)
      try {
        const fireTrigger = httpsCallable(functionsUsEast1, 'handleAutomationTriggers');
        if (values.stage !== deal.stage) {
          fireTrigger({ eventType: 'deal.stage_changed', payload: { contactId: deal.contactId, dealId, fromStage: deal.stage, toStage: values.stage } });
        }
        if (values.disposition) {
          fireTrigger({ eventType: 'activity.disposition_logged', payload: { contactId: deal.contactId, dealId, disposition: values.disposition } });
        }
      } catch (_) {}

      // INSTANT UPDATE: Update deal state immediately
      setDeal(prev => ({
        ...prev,
        stage: values.stage,
        nextAction: values.nextAction || '',
        nextActionDue: values.nextActionDue || null,
        isAtRisk: false,
      }));

      // INSTANT UPDATE: Add new activity to timeline immediately
      if (newActivity) {
        const transformedActivity = {
          id: newActivity.id,
          text: newActivity.content,
          authorId: newActivity.author_id,
          timestamp: newActivity.created_at,
          activityType: newActivity.type,
          ...(newActivity.metadata || {}),
        };
        setActivities(prev => [transformedActivity, ...prev]);
        setTimelineRefreshKey((k) => k + 1);
      }

      toast({ title: 'Activity logged', status: 'success', duration: 3000 });

      // Next Step = "Nurture" on a deal whose source maps to a nurture campaign:
      // prompt to move the lead into nurture (and out of its main campaign).
      // Takes precedence over the generic task prompt — the nurture sequence
      // creates its own follow-up tasks.
      const nurtureRoute = values.nextAction === 'nurture'
        ? getNurtureRouteForSource(deal.source)
        : null;

      if (nurtureRoute) {
        setNurtureRouteData(nurtureRoute);
        onNurtureOpen();
      } else if (values.nextAction && values.nextActionDue) {
        setTaskPromptData({ nextAction: values.nextAction, nextActionDue: values.nextActionDue, dealName: deal.dealName });
        setShowTaskPrompt(true);
      }
      
      actions.resetForm({
        values: {
          ...initialValues,
          stage: values.stage,
          nextAction: values.nextAction,
          nextActionDue: values.nextActionDue,
          notes: '',
          disposition: '',
        },
      });
    } catch (error) {
      toast({ title: 'Error updating deal', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  // V3.0: handleSaveEdits now saves the sidebar data to Supabase
  const handleSaveEdits = async () => {
    if (!isSupabaseConfigured()) {
      toast({ title: 'Database not configured', status: 'error', duration: 5000 });
      return;
    }

    setIsSaving(true);
    try {
      // Update contact in Supabase
      if (deal.contactId) {
        const { error: contactError } = await supabase
          .from('crm_contacts')
          .update({
            first_name: editedContact.firstName,
            last_name: editedContact.lastName,
            email: editedContact.email ? editedContact.email.toLowerCase() : null,
            phone: editedContact.phone,
          })
          .eq('id', deal.contactId);

        if (contactError) throw contactError;
      }
      
      // Parse nested objects
      const propAddr = editedDeal.propertyAddress || {};
      const buyerAgent = editedDeal.buyerAgent || {};
      const sellerAgent = editedDeal.sellerAgent || {};
      const titleCompany = editedDeal.titleCompany || {};
      
      // Update deal in Supabase
      const { error: dealError } = await supabase
        .from('crm_deals')
        .update({
          amount: parseFloat(editedDeal.amount) || 0,
          product: editedDeal.product,
          source_medium: editedDeal.source,
          estimated_closing_date: editedDeal.estimatedClosingDate ? new Date(editedDeal.estimatedClosingDate).toISOString().split('T')[0] : null,
          arive_id: editedDeal.ariveId || null,
          // Loan details
          loan_number: editedDeal.loanNumber || null,
          lender: editedDeal.lender || null,
          loan_amount: editedDeal.loanAmount ? parseFloat(editedDeal.loanAmount) : null,
          down_payment: editedDeal.downPayment ? parseFloat(editedDeal.downPayment) : null,
          interest_rate: editedDeal.interestRate ? parseFloat(editedDeal.interestRate) : null,
          loan_term: editedDeal.loanTerm ? parseInt(editedDeal.loanTerm) : null,
          // Property address
          property_street: propAddr.street || null,
          property_city: propAddr.city || null,
          property_state: propAddr.state || null,
          property_zip: propAddr.zip || null,
          property_type: editedDeal.propertyType || null,
          occupancy_type: editedDeal.occupancyType || 'primary_residence',
          // Buyer agent
          buyer_agent_name: buyerAgent.name || null,
          buyer_agent_email: buyerAgent.email || null,
          buyer_agent_phone: buyerAgent.phone || null,
          buyer_agent_company: buyerAgent.company || null,
          // Seller agent
          seller_agent_name: sellerAgent.name || null,
          seller_agent_email: sellerAgent.email || null,
          seller_agent_phone: sellerAgent.phone || null,
          seller_agent_company: sellerAgent.company || null,
          // Title company
          title_company_name: titleCompany.name || null,
          title_company_contact: titleCompany.contact || null,
          title_company_email: titleCompany.email || null,
          title_company_phone: titleCompany.phone || null,
          // Other fields
          purchase_or_refi: editedDeal.purchaseOrRefi || null,
          loan_purpose: editedDeal.loanPurpose || null,
          credit_score: editedDeal.creditScore ? parseInt(editedDeal.creditScore) : null,
          app_date: editedDeal.appDate ? new Date(editedDeal.appDate).toISOString().split('T')[0] : null,
          // Tags are handled separately by TagSelector
        })
        .eq('id', dealId);

      if (dealError) throw dealError;

      try {
        const fireTrigger = httpsCallable(functionsUsEast1, 'handleAutomationTriggers');
        const trackFields = ['amount', 'product', 'lender', 'loanPurpose', 'creditScore', 'estimatedClosingDate', 'purchaseOrRefi', 'propertyState'];
        for (const field of trackFields) {
          if (editedDeal[field] !== undefined && editedDeal[field] !== deal[field]) {
            fireTrigger({ eventType: 'deal.field_updated', payload: { contactId: deal.contactId, dealId, fieldName: field } });
            break;
          }
        }
      } catch (_) {}

      // Fire contact.updated when contact info changed (mirrors ContactDetailDrawer)
      try {
        const fireTrigger = httpsCallable(functionsUsEast1, 'handleAutomationTriggers');
        for (const field of ['email', 'phone']) {
          if (editedContact[field] !== undefined && editedContact[field] !== contact[field]) {
            fireTrigger({ eventType: 'contact.updated', payload: { contactId: deal.contactId, fieldName: field } });
            break;
          }
        }
      } catch (_) {}
      
      // Update local deal state immediately (don't wait for realtime subscription)
      setDeal(prev => ({
        ...prev,
        ...editedDeal,
        // Ensure nested objects are properly merged
        propertyAddress: { ...prev.propertyAddress, ...editedDeal.propertyAddress },
        buyerAgent: { ...prev.buyerAgent, ...editedDeal.buyerAgent },
        sellerAgent: { ...prev.sellerAgent, ...editedDeal.sellerAgent },
        titleCompany: { ...prev.titleCompany, ...editedDeal.titleCompany },
      }));
      
      // Update local contact state if changed
      if (deal.contactId) {
        setContact(prev => ({
          ...prev,
          firstName: editedContact.firstName,
          lastName: editedContact.lastName,
          email: editedContact.email,
          phone: editedContact.phone,
        }));
      }
      
      // Add activity log entry
      const activityData = {
        deal_id: dealId,
        contact_id: deal.contactId || null,
        author_id: currentUser.uid,
        type: 'edit',
        content: 'Lead details updated',
        metadata: {
          authorName: currentUser.displayName || currentUser.email,
          authorPhotoURL: currentUser.photoURL || '',
          changes: ['Contact and deal information edited'],
        },
      };

      const { data: newActivity, error: activityError } = await supabase
        .from('crm_activities')
        .insert(activityData)
        .select()
        .single();

      if (activityError) throw activityError;
      
      // INSTANT UPDATE: Add new activity to timeline immediately
      if (newActivity) {
        const transformedActivity = {
          id: newActivity.id,
          text: newActivity.content,
          authorId: newActivity.author_id,
          timestamp: newActivity.created_at,
          activityType: newActivity.type,
          ...(newActivity.metadata || {}),
        };
        setActivities(prev => [transformedActivity, ...prev]);
        setTimelineRefreshKey((k) => k + 1);
      }
      
      toast({ title: 'Changes saved', status: 'success', duration: 2000 });
      setIsEditing(false);
    } catch (error) {
      toast({ title: 'Error saving changes', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setIsSaving(false);
    }
  };
  
  // handleDelete uses Supabase
  const handleDelete = async () => {
    if (!isSupabaseConfigured()) {
      toast({ title: 'Database not configured', status: 'error', duration: 5000 });
      return;
    }

    try {
      // Delete deal first (activities will cascade delete due to FK constraint)
      const { error: dealError } = await supabase
        .from('crm_deals')
        .delete()
        .eq('id', dealId);

      if (dealError) throw dealError;

      // Optionally delete contact (be careful - contact may be linked to other deals)
      // For now, only delete if this was the only deal for this contact
      if (deal.contactId) {
        const { data: otherDeals } = await supabase
          .from('crm_deals')
          .select('id')
          .eq('contact_id', deal.contactId)
          .neq('id', dealId);

        if (!otherDeals || otherDeals.length === 0) {
          await supabase
            .from('crm_contacts')
            .delete()
            .eq('id', deal.contactId);
        }
      }

      toast({ title: 'Lead deleted', status: 'success', duration: 3000 });
      onDeleteClose();
      history.push('/crm/my-deals');
    } catch (error) {
      toast({ title: 'Error deleting lead', description: error.message, status: 'error', duration: 5000 });
    }
  };
  const handleCopyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text || '');
    toast({ title: `${label} copied`, status: 'success', duration: 2000 });
  };
  const buildCRMNotesSummary = (activitiesList) => {
    if (!activitiesList || activitiesList.length === 0) return 'No CRM activity history available.';
    const summary = activitiesList.slice(0, 5).map((activity) => {
      const time = activity.timestamp?.seconds ? moment(activity.timestamp.seconds * 1000).format('MM/DD/YY h:mm A') : 'Unknown time';
      const author = activity.authorName || 'Unknown';
      const text = activity.text || activity.description || 'Activity logged';
      const disposition = activity.disposition ? ` [${dispositions.find((d) => d.value === activity.disposition)?.label || activity.disposition}]` : '';
      return `[${time}] ${author}${disposition}: ${text}`;
    }).join('\n');
    return `=== CRM HISTORY (Last ${Math.min(5, activitiesList.length)} interactions) ===\n${summary}\n\n--- Lead converted from CRM ---`;
  };
  const handleCreateTaskFromPrompt = async (option) => {
    if (option === 'auto') {
      // Validate user is logged in
      if (!currentUser?.uid) {
        toast({ title: 'Failed to create task', description: 'You must be logged in to create tasks.', status: 'error', duration: 5000 });
        setShowTaskPrompt(false);
        setTaskPromptData(null);
        return;
      }

      const taskData = {
        title: `${nextActions.find((a) => a.value === taskPromptData.nextAction)?.label || taskPromptData.nextAction}: ${taskPromptData.dealName}`,
        description: `Next action for ${taskPromptData.dealName}`,
        type: 'crm',
        priority: 'medium',
        status: 'pending',
        assignedTo: currentUser.uid,
        createdBy: currentUser.uid,
        dueDate: taskPromptData.nextActionDue,
        dueTime: '',
        relatedTo: { type: 'deal', id: dealId, name: deal.dealName },
      };
      console.log('[DealPage] Creating task with currentUser.uid:', currentUser.uid);
      const result = await createTask(taskData);
      if (result.success) {
        toast({ title: 'Task created', status: 'success', duration: 3000 });
      } else {
        toast({ title: 'Failed to create task', description: result.error || 'Unknown error', status: 'error', duration: 5000 });
      }
    } else if (option === 'manual') {
      onUnifiedEventOpen();
    }
    setShowTaskPrompt(false);
    setTaskPromptData(null);
  };
  const handleEditTask = (task) => {
    setEditingTask(task);
    onTaskDetailClose();
    onUnifiedEventOpen();
  };
  
  // ... (Loading state remains the same) ...
  if (loading || !deal || !contact) {
    return (
      <Box bg={bgColor} minH="100vh">
        <Container maxW="container.xl" py={8}>
          <Center h="60vh" flexDir="column">
            <Spinner size="xl" color="blue.500" thickness="4px" mb={4} />
            <Text color="gray.600">Loading deal...</Text>
          </Center>
        </Container>
      </Box>
    );
  }

  // No access (graceful, no data leaked). While the viewer's book shares are
  // still resolving, keep spinning instead of flashing a denial that a share
  // would immediately reverse.
  if (!canSeeDeal) {
    return (
      <Box bg={bgColor} minH="100vh">
        <Container maxW="container.xl" py={8}>
          <Center h="60vh" flexDir="column" px={6} textAlign="center">
            {myDealScopeLoading ? (
              <>
                <Spinner size="xl" color="blue.500" thickness="4px" mb={4} />
                <Text color="gray.600">Loading deal...</Text>
              </>
            ) : (
              <>
                <Text fontSize="lg" fontWeight="bold" mb={1}>
                  You don't have access to this deal
                </Text>
                <Text color="gray.500" fontSize="sm" mb={6} maxW="420px">
                  It belongs to another user's book. Ask them (or a CRM admin) to
                  share it with you, or add you to their team.
                </Text>
                <Button colorScheme="blue" onClick={() => history.push('/crm/my-deals')}>
                  Back to My Deals
                </Button>
              </>
            )}
          </Center>
        </Container>
      </Box>
    );
  }

  // ... (initialValues for Formik remain the same) ...
  const initialValues = {
    stage: deal.stage || '',
    disposition: '',
    nextAction: '',
    nextActionDue: null,
    notes: '',
  };
  
  // Hide "Submit to Hub" once the deal is in any active loan workflow stage or a terminal stage.
  // (Arive-aligned stages + legacy slugs for Phase A backwards compat.)
  const POST_APP_OR_CLOSED_STAGES = [
    'loan-application-intake', 'loan-app-intake',
    'loan-qualification', 'loan-preapproved',
    'loan-setup', 'loan-disclosure-sent', 'loan-submitted',
    'loan-approved-conditions', 'loan-resubmittal',
    'loan-clear-to-close', 'loan-docs-out', 'loan-docs-signed',
    'loan-funded', 'loan-brokercheck-received', 'loan-finalized',
    'loan-processing', 'deal-won', 'deal-lost',
  ];
  const showSubmitToHubButton = deal && !POST_APP_OR_CLOSED_STAGES.includes(deal.stage);

  // Single string (not JSX fragments) so ShrinkToFitText's effect dep compares
  // by value and only re-fits when the name actually changes.
  const contactFullName = `${contact.firstName} ${contact.middleInitial ? `${contact.middleInitial}. ` : ''}${contact.lastName}`;

  // --- REFACTORED JSX (v3.0) ---
  return (
    <Box bg={bgColor} minH="100vh" pb={{ base: 28, lg: 8 }} overflowX="clip">
      
      {/* Sticky Header */}
      <Box
        bg={headerBG}
        py={{ base: 3, md: 4 }}
        px={{ base: 3, md: 6, lg: 8 }}
        shadow="sm"
        position="sticky"
        top={0}
        zIndex={100}
        borderBottomWidth="1px"
        borderColor={borderColor}
      >
        {/* Mobile stacks info + actions side by side with tags on their own row;
            lg keeps the original three-column info | tags | actions layout. */}
        <Grid
          templateAreas={{ base: `"info actions" "tags tags"`, lg: `"info tags actions"` }}
          templateColumns={{
            base: 'minmax(0, 1fr) auto',
            lg: 'minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1.5fr)',
          }}
          gap={{ base: 2, lg: 4 }}
          maxW="container.xl"
          mx="auto"
          alignItems="start"
        >
          {/* Column 1: Contact & Deal Info */}
          <VStack gridArea="info" align="stretch" spacing={2} minW={0}>
            {/* Row 1: Back Button + Name + Stage */}
            <HStack spacing={{ base: 2, md: 3 }} minW={0}>
              <Tooltip label="Back">
                <IconButton icon={<FiArrowLeft />} aria-label="Back" onClick={() => history.goBack()} variant="ghost" size="md" />
              </Tooltip>
              {workQueue.length > 0 && (
                <Tooltip label={nextLeadTooltipLabel}>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    variant="outline"
                    rightIcon={<FiArrowRight />}
                    onClick={handleNextLead}
                    isDisabled={workQueue.length === 1 && currentQueueIndex === 0}
                    display={{ base: 'none', md: 'flex' }}
                  >
                    {nextLeadButtonLabel}{' '}
                    {currentQueueIndex >= 0
                      ? `(${currentQueueIndex + 1}/${workQueue.length})`
                      : `(${workQueue.length})`}
                  </Button>
                </Tooltip>
              )}
              {workQueue.length > 0 && (
                <Tooltip label={nextLeadTooltipLabel}>
                  <IconButton
                    icon={<FiArrowRight />}
                    aria-label={nextLeadButtonLabel}
                    size="sm"
                    colorScheme="blue"
                    variant="outline"
                    onClick={handleNextLead}
                    isDisabled={workQueue.length === 1 && currentQueueIndex === 0}
                    display={{ base: 'flex', md: 'none' }}
                  />
                </Tooltip>
              )}
              <Avatar size="md" name={`${contact.firstName} ${contact.lastName}`} display={{ base: 'none', lg: 'flex' }} />
              <VStack align="stretch" spacing={1} flex="1 1 0" minW={0}>
                {isCRMAdmin ? (
                  <HStack minW={0}>
                    <Tooltip label="Click to reassign this deal">
                      <Badge
                        colorScheme="purple"
                        fontSize="xs"
                        cursor="pointer"
                        maxW="100%"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        _hover={{ opacity: 0.8 }}
                        onClick={() => {
                          setNotifyNewOwner(false);
                          onReassignOpen();
                        }}
                      >
                        <Text as="span" display={{ base: 'none', md: 'inline' }}>Assigned To: </Text>
                        {ownerName}
                        <Text as="span" display={{ base: 'none', md: 'inline' }}> (change)</Text>
                      </Badge>
                    </Tooltip>
                  </HStack>
                ) : deal.ownerId ? (
                  <HStack minW={0}>
                    <Badge
                      colorScheme="purple"
                      fontSize="xs"
                      maxW="100%"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      <Text as="span" display={{ base: 'none', md: 'inline' }}>Assigned To: </Text>
                      {ownerName}
                    </Badge>
                  </HStack>
                ) : null}
                <HStack minW={0} spacing={2} data-tour="deal-header">
                  <ShrinkToFitText
                    fontWeight="bold"
                    maxFontPx={nameMaxFontPx}
                    minFontPx={11}
                    refitKey={deal.stage}
                    title={contactFullName}
                  >
                    {contactFullName}
                  </ShrinkToFitText>
                  <Badge
                    colorScheme={STAGES.find((s) => s.value === deal.stage)?.color || 'gray'}
                    fontSize={{ base: 'xs', md: 'sm' }}
                    px={{ base: 2, md: 3 }}
                    py={1}
                    flexShrink={0}
                    maxW="45%"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {STAGES.find((s) => s.value === deal.stage)?.label || deal.stage}
                  </Badge>
                  {/* Record scope is an admin-only concept — the badge is never
                      shown to non-admins (e.g. company_allowlist grantees) */}
                  {isCRMAdmin && deal.recordScope === 'company' && (
                    <Tooltip label="Company record — visible to all CRM admins">
                      <Badge colorScheme="purple" variant="solid" fontSize="xs" flexShrink={0}>
                        <HStack spacing={1}>
                          <Icon as={FiBriefcase} boxSize={3} />
                          <Text as="span" display={{ base: 'none', md: 'inline' }}>Company</Text>
                        </HStack>
                      </Badge>
                    </Tooltip>
                  )}
                </HStack>
              </VStack>
            </HStack>

            {/* Mobile-only toggle for the contact/loan detail rows below.
                Hidden while editing, since editing force-expands them. */}
            <Button
              size="xs"
              variant="ghost"
              alignSelf="flex-start"
              display={{ base: isEditing ? 'none' : 'inline-flex', lg: 'none' }}
              rightIcon={isMobileDetailsOpen ? <FiChevronUp /> : <FiChevronDown />}
              onClick={onMobileDetailsToggle}
            >
              {isMobileDetailsOpen ? 'Hide details' : 'Details'}
            </Button>

            {/* Rows 2-4: always visible at lg, collapsible on mobile. Single mount so
                DncControl doesn't end up with two copies of its optimistic state.
                Force-expanded while editing so these inputs stay reachable. */}
            <Box display={{ base: (isMobileDetailsOpen || isEditing) ? 'block' : 'none', lg: 'block' }}>
              <VStack align="stretch" spacing={2}>

                {/* Row 2: Email */}
                {isEditing ? (
                  <HStack spacing={2} pl={{ base: 0, lg: 12 }} minW={0}>
                    <Icon as={FiMail} boxSize={3} />
                    <Input
                      size="xs"
                      type="email"
                      value={editedContact.email || ''}
                      onChange={(e) => setEditedContact({ ...editedContact, email: e.target.value })}
                      placeholder="name@example.com"
                      minW={0}
                      maxW="260px"
                      bg="white"
                    />
                  </HStack>
                ) : contact.email ? (
                  <HStack
                    spacing={1}
                    cursor="pointer"
                    onClick={() => handleCopyToClipboard(contact.email, 'Email')}
                    _hover={{ color: 'purple.500' }}
                    transition="color 0.2s"
                    pl={{ base: 0, lg: 12 }}
                    minW={0}
                  >
                    <Icon as={FiMail} boxSize={3} flexShrink={0} />
                    <Text fontSize="sm" noOfLines={1} minW={0}>{contact.email}</Text>
                  </HStack>
                ) : null}

                {/* Row 3: Phone + DNC */}
                <HStack spacing={{ base: 2, lg: 4 }} pl={{ base: 0, lg: 12 }} flexWrap="wrap" gap={2} minW={0}>
                  {isEditing ? (
                    <HStack spacing={2}>
                      <Icon as={FiPhone} boxSize={3} />
                      <Input
                        size="xs"
                        type="tel"
                        value={editedContact.phone || ''}
                        onChange={(e) => setEditedContact({ ...editedContact, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        maxW="180px"
                        bg="white"
                      />
                    </HStack>
                  ) : contact.phone ? (
                    <HStack
                      spacing={1}
                      cursor="pointer"
                      onClick={() => handleCopyToClipboard(contact.phone, 'Phone')}
                      _hover={{ color: 'blue.500' }}
                      transition="color 0.2s"
                    >
                      <Icon as={FiPhone} boxSize={3} />
                      <Text fontSize="sm">{contact.phone}</Text>
                    </HStack>
                  ) : null}

                  {/* DNC (4-category: Everything / Call / Email / SMS) */}
                  <DncControl
                    contactId={deal.contactId}
                    contact={contact}
                    onChanged={(next) => setContact((c) => ({ ...c, ...next }))}
                  />
                </HStack>

                {/* Row 4: Lender | Loan # */}
                <HStack spacing={{ base: 2, lg: 4 }} flexWrap="wrap" gap={2} pl={{ base: 0, lg: 12 }} minW={0}>
                  {/* Lender */}
                  {isEditing ? (
                    <HStack spacing={2}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">Lender:</Text>
                      <Input
                        size="xs"
                        value={editedDeal.lender || ''}
                        onChange={(e) => setEditedDeal({ ...editedDeal, lender: e.target.value })}
                        placeholder="Lender Name"
                        maxW="150px"
                        bg="white"
                      />
                    </HStack>
                  ) : deal.lender ? (
                    <HStack spacing={1} px={2} py={1} bg="green.50" borderRadius="md">
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">Lender:</Text>
                      <Text fontSize="xs" fontWeight="bold" color="green.700">{deal.lender}</Text>
                    </HStack>
                  ) : null}

                  {/* Loan Number */}
                  {isEditing ? (
                    <HStack spacing={2}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">Loan #:</Text>
                      <Input
                        size="xs"
                        value={editedDeal.loanNumber || ''}
                        onChange={(e) => setEditedDeal({ ...editedDeal, loanNumber: e.target.value })}
                        placeholder="Loan Number"
                        maxW="150px"
                        bg="white"
                      />
                    </HStack>
                  ) : deal.loanNumber ? (
                    <HStack spacing={1} px={2} py={1} bg="blue.50" borderRadius="md">
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">Loan #:</Text>
                      <Text fontSize="xs" fontWeight="bold" color="blue.700">{deal.loanNumber}</Text>
                    </HStack>
                  ) : null}

                  {/* Arive ID */}
                  {isEditing ? (
                    <HStack spacing={2}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">Arive ID:</Text>
                      <Input
                        size="xs"
                        value={editedDeal.ariveId || ''}
                        onChange={(e) => setEditedDeal({ ...editedDeal, ariveId: e.target.value })}
                        placeholder="Arive ID"
                        maxW="150px"
                        bg="white"
                      />
                    </HStack>
                  ) : deal.ariveId ? (
                    <HStack spacing={1} px={2} py={1} bg="teal.50" borderRadius="md">
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">Arive ID:</Text>
                      <Text fontSize="xs" fontWeight="bold" color="teal.700">{deal.ariveId}</Text>
                    </HStack>
                  ) : null}
                </HStack>
              </VStack>
            </Box>
          </VStack>
          
          {/* Column 2: Tags — centered at lg, own full-width row on mobile */}
          <VStack
            gridArea="tags"
            align={{ base: 'stretch', lg: 'center' }}
            justify="center"
            spacing={2}
            minW={0}
          >
            <TagSelector dealId={dealId} contactId={deal.contactId} currentTags={deal.tags} userId={currentUser?.uid} isEditable={!isEditing} onTagsChange={(newTags) => setDeal(prev => ({ ...prev, tags: newTags }))} />
          </VStack>
          
          {/* Column 3: Action Buttons */}
          <VStack gridArea="actions" align="flex-end" spacing={2} minW={0}>
            <HStack spacing={2} flexWrap="wrap" justify="flex-end">
              {isEditing && (
                <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
              )}
              
              {/* Submit to Hub - Desktop only */}
              {!isEditing && showSubmitToHubButton && (
                <Button size="sm" colorScheme="green" leftIcon={<FiExternalLink />} onClick={onSubmissionOpen} display={{ base: 'none', lg: 'flex' }}>
                  Submit to Hub
                </Button>
              )}
              
              {/* Quick search: jump to another deal / recently viewed - all breakpoints */}
              {!isEditing && (
                <Tooltip label="Find a deal (Cmd+K)" hasArrow>
                  <IconButton
                    aria-label="Find a deal"
                    icon={<FiSearch />}
                    size="md"
                    fontSize="lg"
                    colorScheme="blue"
                    onClick={onQuickSearchOpen}
                  />
                </Tooltip>
              )}

              {/* Open in Arive - Desktop only */}
              {!isEditing && (
                <Button
                  size="sm"
                  colorScheme="teal"
                  leftIcon={<FiExternalLink />}
                  onClick={() => {
                    if (deal.ariveId) {
                      // Any post-application loan stage uses the loan-center deep link;
                      // pre-application stages use the lead deep link.
                      const LOAN_CENTER_STAGES = [
                        'loan-application-intake', 'loan-app-intake',
                        'loan-qualification', 'loan-preapproved',
                        'loan-setup', 'loan-disclosure-sent', 'loan-submitted',
                        'loan-approved-conditions', 'loan-resubmittal',
                        'loan-clear-to-close', 'loan-docs-out', 'loan-docs-signed',
                        'loan-funded', 'loan-brokercheck-received', 'loan-finalized',
                        'loan-processing', 'deal-won', // legacy
                      ];
                      const url = deal.stage && LOAN_CENTER_STAGES.includes(deal.stage)
                        ? `https://future.myarive.com/app/loans/${deal.ariveId}/loan-center`
                        : `https://future.myarive.com/app/leads/${deal.ariveId}/`;
                      window.open(url, '_blank', 'noopener,noreferrer');
                    } else {
                      onArriveOpen();
                    }
                  }}
                  display={{ base: 'none', lg: 'flex' }}
                >
                  {deal.ariveId ? 'Open Arive' : 'Link Arive'}
                </Button>
              )}
              
              {/* Add Task - Desktop only */}
              {!isEditing && (
                <Button size="sm" colorScheme="purple" leftIcon={<FiCalendar />} onClick={onUnifiedEventOpen} display={{ base: 'none', lg: 'flex' }}>
                  Add Task
                </Button>
              )}
              
              {/* Edit/Save Button */}
              <Button
                size="sm"
                leftIcon={isEditing ? <FiSave /> : <FiEdit2 />}
                colorScheme={isEditing ? 'blue' : 'gray'}
                variant={isEditing ? 'solid' : 'outline'}
                onClick={isEditing ? handleSaveEdits : () => setIsEditing(true)}
                isLoading={isSaving}
                display={{ base: 'none', md: 'inline-flex' }}
              >
                {isEditing ? 'Save Changes' : 'Edit'}
              </Button>

              {/* Edit/Save - icon only on mobile, same handlers */}
              <IconButton
                aria-label={isEditing ? 'Save changes' : 'Edit deal'}
                icon={isEditing ? <FiSave /> : <FiEdit2 />}
                size="md"
                colorScheme={isEditing ? 'blue' : 'gray'}
                variant={isEditing ? 'solid' : 'outline'}
                onClick={isEditing ? handleSaveEdits : () => setIsEditing(true)}
                isLoading={isSaving}
                display={{ base: 'inline-flex', md: 'none' }}
              />
              
              {/* Menu - Desktop: Only Delete, Mobile: All actions */}
              {!isEditing && (
                <Menu>
                  <MenuButton as={IconButton} icon={<FiMoreVertical />} size={{ base: 'md', md: 'sm' }} variant="ghost" aria-label="More actions" />
                  <MenuList>
                    {/* Mobile-only actions */}
                    {showSubmitToHubButton && (
                      <MenuItem icon={<FiExternalLink />} onClick={onSubmissionOpen} color="green.600" display={{ base: 'flex', lg: 'none' }}>
                        Submit to Hub
                      </MenuItem>
                    )}
                    <MenuItem 
                      icon={<FiExternalLink />} 
                      onClick={() => {
                        if (deal.ariveId) {
                          const LOAN_CENTER_STAGES = [
                            'loan-application-intake', 'loan-app-intake',
                            'loan-qualification', 'loan-preapproved',
                            'loan-setup', 'loan-disclosure-sent', 'loan-submitted',
                            'loan-approved-conditions', 'loan-resubmittal',
                            'loan-clear-to-close', 'loan-docs-out', 'loan-docs-signed',
                            'loan-funded', 'loan-brokercheck-received', 'loan-finalized',
                            'loan-processing', 'deal-won', // legacy
                          ];
                          const url = deal.stage && LOAN_CENTER_STAGES.includes(deal.stage)
                            ? `https://future.myarive.com/app/loans/${deal.ariveId}/loan-center`
                            : `https://future.myarive.com/app/leads/${deal.ariveId}/`;
                          window.open(url, '_blank', 'noopener,noreferrer');
                        } else {
                          onArriveOpen();
                        }
                      }}
                      color="teal.600"
                      display={{ base: 'flex', lg: 'none' }}
                    >
                      {deal.ariveId ? 'Open Arive' : 'Link Arive'}
                    </MenuItem>
                    <MenuItem icon={<FiCalendar />} onClick={onUnifiedEventOpen} color="purple.600" display={{ base: 'flex', lg: 'none' }}>
                      Add Task
                    </MenuItem>
                    {/* Always visible */}
                    {isCRMAdmin && (
                      <MenuItem icon={<FiBriefcase />} color="purple.600" onClick={handleToggleCompanyScope}>
                        {deal.recordScope === 'company' ? 'Convert to Personal Record' : 'Mark as Company Record'}
                      </MenuItem>
                    )}
                    <MenuItem icon={<FiTrash2 />} color="red.500" onClick={onDeleteOpen}>
                      Delete Lead
                    </MenuItem>
                  </MenuList>
                </Menu>
              )}
            </HStack>
          </VStack>
        </Grid>
      </Box>

      {/* Main Content Grid (V3.0) */}
      {/* px={0} at base: the app shell's Container (Layout.jsx) already pays 16px per side. */}
      <Container maxW="container.xl" py={{ base: 4, md: 6 }} px={{ base: 0, md: 6 }}>
        <AriveAutoSuggestBanner deal={deal} onLinked={() => {}} />
        <Grid
          templateColumns={{ base: 'minmax(0, 1fr)', lg: 'minmax(0, 1.8fr) minmax(0, 1.2fr)' }}
          gap={{ base: 4, lg: 6 }}
          alignItems="start"
        >
          
          {/* Left Column (Working Area) */}
          <GridItem minW={0}>
            <Box bg={cardBG} borderRadius="xl" borderWidth="1px" borderColor={borderColor} shadow="sm">
              <Tabs colorScheme="blue">
                {/* overflowX="auto" also makes this a scroll container, which drops its
                    min-content contribution to 0 so four tabs can't widen the page. */}
                <TabList
                  px={{ base: 2, md: 6 }}
                  overflowX="auto"
                  overflowY="hidden"
                  sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
                >
                  <Tab flexShrink={0} whiteSpace="nowrap" px={{ base: 3, md: 4 }} data-tour="deal-tab-activity">
                    <HStack spacing={2}>
                      <Icon as={FiCheckCircle} />
                      <Text display={{ base: 'none', md: 'inline' }}>Activity & Update</Text>
                      <Text display={{ base: 'inline', md: 'none' }}>Activity</Text>
                    </HStack>
                  </Tab>
                  <Tab flexShrink={0} whiteSpace="nowrap" px={{ base: 3, md: 4 }} data-tour="deal-tab-tasks">
                    <HStack spacing={2}>
                      <Icon as={FiCalendar} />
                      <Text>Tasks</Text>
                      <Badge ml={2} colorScheme="blue">{activeDealTasks.length}</Badge>
                    </HStack>
                  </Tab>
                  {dialpadEnabled && (
                    <Tab flexShrink={0} whiteSpace="nowrap" px={{ base: 3, md: 4 }}>
                      <HStack spacing={2}>
                        <Icon as={FiPhone} />
                        <Text>Dialpad</Text>
                      </HStack>
                    </Tab>
                  )}
                  <Tab flexShrink={0} whiteSpace="nowrap" px={{ base: 3, md: 4 }} data-tour="deal-tab-campaigns">
                    <HStack spacing={2}>
                      <Icon as={FiZap} />
                      <Text>Campaigns</Text>
                      {activeCampaignCount > 0 && (
                        <Badge ml={2} colorScheme="blue">{activeCampaignCount}</Badge>
                      )}
                    </HStack>
                  </Tab>
                </TabList>
                <TabPanels>
                  
                  {/* TAB 1: ACTIVITY & UPDATE (COMBINED) */}
                  <TabPanel px={{ base: 3, md: 6 }} py={{ base: 4, md: 6 }}>
                    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
                      {({ handleSubmit, values, setFieldValue, errors, touched }) => (
                        <Box as="form" onSubmit={handleSubmit}>
                          <VStack spacing={6} align="stretch">
                            <Box data-tour="deal-log-activity">
                              <VStack spacing={6} align="stretch">
                                <Heading size={{ base: 'sm', md: 'md' }} mb={2}>Log Activity</Heading>
                                <SelectControl name="stage" label="Stage / Status">
                                  {getStagesForPicker(deal.stage).map((stage) => <option key={stage.value} value={stage.value}>{stage.label}</option>)}
                                </SelectControl>
                                <Grid templateColumns={{ base: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' }} gap={4}>
                                  <SelectControl name="disposition" label="Disposition" minW={0}>
                                    <option value="">Select Disposition</option>
                                    {dispositions.map((disp) => <option key={disp.value} value={disp.value}>{disp.label}</option>)}
                                  </SelectControl>
                                  <SelectControl name="nextAction" label="Next Step" minW={0}>
                                    {nextActions.map((action) => <option key={action.value} value={action.value}>{action.label}</option>)}
                                  </SelectControl>
                                  {/* react-datepicker's wrapper is display:inline-block, so the
                                      input won't fill the cell. Scoped here to avoid changing
                                      DatePickerField for every other form in the app. */}
                                  <Box
                                    minW={0}
                                    sx={{
                                      '.react-datepicker-wrapper': { width: '100%' },
                                      '.react-datepicker__input-container': { width: '100%' },
                                      '.react-datepicker__input-container input': { width: '100%', minWidth: 0 },
                                    }}
                                  >
                                    <DatePickerField name="nextActionDue" label="Due Date" value={values.nextActionDue} onChange={(date) => setFieldValue('nextActionDue', date)} />
                                  </Box>
                                </Grid>
                                <TextareaControl name="notes" label="Notes" minW={0} textareaProps={{ 
                                  placeholder: "Log call details, outcomes, or notes...", 
                                  rows: 5, 
                                  maxLength: charLimit,
                                  minWidth: 0,
                                }} />
                                <Button type="submit" colorScheme="blue" size="lg" isLoading={isSubmitting} rightIcon={<FiCheckCircle />}>
                                  Save & Log Activity
                                </Button>
                              </VStack>
                            </Box>
                            <Divider my={4} />
                            <Box data-tour="deal-timeline">
                              <VStack spacing={6} align="stretch">
                                <Heading size="md" mb={2}>Activity Timeline</Heading>
                                {/* Unified timeline: this deal's activity PLUS the
                                    contact's notes (badged), other-deal notes (badged
                                    with deal name), and AI call summaries. */}
                                <UnifiedActivityTimeline
                                  contactId={deal?.contactId}
                                  dealIds={dealId ? [dealId] : []}
                                  primaryDealId={dealId}
                                  refreshKey={timelineRefreshKey}
                                  maxHeight="500px"
                                />
                              </VStack>
                            </Box>
                          </VStack>
                        </Box>
                      )}
                    </Formik>
                  </TabPanel>

                  {/* TAB 2: TASKS */}
                  <TabPanel px={{ base: 3, md: 6 }} py={{ base: 4, md: 6 }}>
                    <VStack align="stretch" spacing={4}>
                      <HStack justify="space-between" flexWrap="wrap" gap={2} data-tour="deal-tasks">
                        <Heading size={{ base: 'sm', md: 'md' }}>Tasks for this Deal</Heading>
                        <Button size="sm" leftIcon={<FiPlus />} colorScheme="blue" onClick={onUnifiedEventOpen}>New Task</Button>
                      </HStack>
                      {dealTasks.length === 0 ? (
                        <Center py={12}>
                          <VStack>
                            <Icon as={FiCalendar} boxSize={12} color="gray.300" mb={3} />
                            <Text color="gray.500" fontSize="sm">No tasks for this deal yet</Text>
                            <Text fontSize="xs" color="gray.400">Create a task to track follow-ups</Text>
                          </VStack>
                        </Center>
                      ) : (
                        <TaskList
                          tasks={dealTasks}
                          onComplete={completeTask}
                          onReopen={reopenTask}
                          onEdit={handleEditTask}
                          onDelete={deleteTask}
                          onClick={(task) => {
                            setSelectedTask(task);
                            onTaskDetailOpen();
                          }}
                        />
                      )}
                    </VStack>
                  </TabPanel>
                  
                  {/* TAB 3: DIALPAD */}
                  {dialpadEnabled && (
                    <TabPanel px={{ base: 3, md: 6 }} py={{ base: 4, md: 6 }}>
                      <VStack align="stretch" spacing={4}>
                        <Heading size={{ base: 'sm', md: 'md' }}>Dialpad History</Heading>
                        <CallHistoryPanel contactId={deal.contactId} dealId={dealId} />
                      </VStack>
                    </TabPanel>
                  )}

                  {/* TAB 4: CAMPAIGNS */}
                  <TabPanel px={{ base: 3, md: 6 }} py={{ base: 4, md: 6 }} data-tour="deal-campaigns">
                    <ContactCampaignPanel contactId={deal?.contactId} onActiveCountChange={setActiveCampaignCount} />
                  </TabPanel>

                </TabPanels>
              </Tabs>
            </Box>
          </GridItem>

          {/* Right Column (Info Sidebar - V3.0) */}
          <GridItem
            minW={0}
            position={{ base: 'relative', lg: 'sticky' }}
            top={{ base: 'auto', lg: '130px' }}
            alignSelf="start"
            maxHeight={{ base: 'none', lg: 'calc(100vh - 150px)' }}
            overflowY={{ base: 'visible', lg: 'auto' }}
          >
            <VStack align="stretch" spacing={6}>
              
              {/* Quick Actions Card */}
              <Box bg={cardBG} borderRadius="xl" borderWidth="1px" borderColor={borderColor} shadow="sm" p={4} data-tour="deal-quick-actions">
                <VStack align="stretch" spacing={2}>
                  <Heading size="sm" mb={1}>Quick Actions</Heading>
                  <Divider />
                  {dialpadEnabled && (
                    <>
                      <DialpadCallButton 
                        phoneNumber={contact.phone} 
                        contactName={`${contact.firstName} ${contact.lastName}`} 
                        dealId={dealId} 
                        contactId={deal.contactId} 
                        isDisabled={contact.dnc || contact.dncCall}
                        size="sm"
                      />
                      <DialpadSMSButton 
                        phoneNumber={contact.phone} 
                        contactName={`${contact.firstName} ${contact.lastName}`} 
                        dealId={dealId} 
                        contactId={deal.contactId} 
                        deal={deal}
                        contact={contact}
                        isDisabled={contact.dnc || contact.dncSms}
                        size="sm"
                      />
                    </>
                  )}
                  {!dialpadEnabled && (
                    <>
                      <Button 
                        leftIcon={<FiPhone />} 
                        size="sm" 
                        colorScheme="blue" 
                        w="full" 
                        isDisabled={contact.dnc || contact.dncCall} 
                        as={Link} 
                        href={`tel:${contact.phone}`}
                      >
                        Call
                      </Button>
                      <Button 
                        leftIcon={<FiMessageSquare />} 
                        size="sm" 
                        colorScheme="green" 
                        w="full" 
                        isDisabled={contact.dnc || contact.dncSms}
                        onClick={onSMSOpen}
                      >
                        Text
                      </Button>
                    </>
                  )}
                  <Button 
                    leftIcon={<FiMail />} 
                    size="sm" 
                    colorScheme="purple" 
                    w="full" 
                    isDisabled={contact.dnc || contact.dncEmail}
                    onClick={onEmailOpen}
                  >
                    Email
                  </Button>
                  
                  {(contact.dnc || contact.dncCall || contact.dncEmail || contact.dncSms) && (
                    <Alert status="error" size="sm" borderRadius="md">
                      <AlertIcon />
                      <Text fontSize="xs">
                        {contact.dnc
                          ? 'DNC: all outreach disabled'
                          : `DNC: ${[contact.dncCall && 'calling', contact.dncSms && 'texting', contact.dncEmail && 'emailing'].filter(Boolean).join(', ')} disabled`}
                      </Text>
                    </Alert>
                  )}
                </VStack>
              </Box>

              {/* Arive LOS Panel */}
              <Box data-tour="deal-arive">
                <AriveSyncPanel deal={deal} onDealUpdate={() => {}} />
              </Box>

              {/* Deal Details Card (New V3.0) */}
              <Box data-tour="deal-details">
                <DealDetailsCard
                  contact={contact}
                  deal={deal}
                  isEditing={isEditing}
                  editedContact={editedContact}
                  setEditedContact={setEditedContact}
                  editedDeal={editedDeal}
                  setEditedDeal={setEditedDeal}
                  onToggleEdit={() => setIsEditing(true)}
                />
              </Box>
              
            </VStack>
          </GridItem>
        </Grid>
      </Container>

      {/* --- Modals & Drawers (All remain the same) --- */}
      <Modal isOpen={showTaskPrompt} onClose={() => setShowTaskPrompt(false)} isCentered>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent>
          <ModalHeader>Create Task for Next Action?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <VStack align="flex-start" spacing={1} flex={1}>
                <Text fontSize="sm" fontWeight="semibold">Would you like to create a task?</Text>
                <Text fontSize="xs">Next Action: {taskPromptData && nextActions.find((a) => a.value === taskPromptData.nextAction)?.label}</Text>
                <Text fontSize="xs">Due: {taskPromptData && moment(taskPromptData.nextActionDue).format('MMM DD, YYYY')}</Text>
              </VStack>
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => handleCreateTaskFromPrompt('no')}>No</Button>
            <Button variant="outline" mr={2} onClick={() => handleCreateTaskFromPrompt('manual')}>Create Manually</Button>
            <Button colorScheme="blue" onClick={() => handleCreateTaskFromPrompt('auto')}>Yes, Create Task</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      
      <MoveToNurtureModal
        isOpen={isNurtureOpen}
        onClose={onNurtureClose}
        deal={deal}
        contact={contact}
        route={nurtureRouteData}
        onComplete={({ newDealTags, activity }) => {
          if (Array.isArray(newDealTags)) {
            setDeal(prev => ({ ...prev, tags: newDealTags }));
          }
          if (activity) {
            setActivities(prev => [activity, ...prev]);
            setTimelineRefreshKey((k) => k + 1);
          }
        }}
      />

      <UnifiedEventModal 
        isOpen={isUnifiedEventOpen} 
        onClose={() => {
          onUnifiedEventClose();
          setEditingTask(null);
        }}
        onSave={async () => {
          // Refresh happens automatically via the useDealTasks realtime subscription
          toast({ title: editingTask ? 'Task/Event updated' : 'Task/Event created', status: 'success', duration: 2000 });
          setEditingTask(null);
        }}
        initialEventType="task"
        prefilledEntity={{
          type: 'deal',
          data: deal,
          contact: contact,
        }}
        editingTask={editingTask}
        crmUsers={crmUsers}
      />
      
      <TaskDetailModal
        isOpen={isTaskDetailOpen}
        onClose={onTaskDetailClose}
        task={selectedTask}
        onEdit={handleEditTask}
        onDelete={async (taskId) => {
          const result = await deleteTask(taskId);
          if (result.success) {
            toast({ title: 'Task deleted', status: 'success', duration: 2000 });
            onTaskDetailClose();
          }
          return result;
        }}
        onComplete={completeTask}
        onReopen={reopenTask}
      />
      
      <ArriveLinkModal 
        isOpen={isArriveOpen} 
        onClose={onArriveClose} 
        deal={deal}
        onSave={(newAriveId) => setDeal(prev => ({ ...prev, ariveId: newAriveId }))}
      />

      <DealQuickSearchModal
        isOpen={isQuickSearchOpen}
        onClose={onQuickSearchClose}
        currentDealId={dealId}
        isCRMAdmin={isCRMAdmin}
        crmUsers={crmUsers}
        recentViews={recentViews}
      />

      <EmailTemplateModal
        isOpen={isEmailOpen}
        onClose={onEmailClose}
        deal={deal}
        contact={contact}
      />

      <SMSTemplateModal
        isOpen={isSMSOpen}
        onClose={onSMSClose}
        deal={deal}
        contact={contact}
      />

      {/* DTC Lost Deal Reason Modal */}
      <LostDealReasonModal
        isOpen={isLostReasonOpen}
        onClose={() => {
          onLostReasonClose();
          setPendingLostStageUpdate(null);
        }}
        deal={deal}
        contact={contact}
        onConfirm={handleLostReasonConfirm}
        isLoading={isLostReasonSubmitting}
      />

      {/* Reassign Deal Modal (CRM Admins only) */}
      <Modal isOpen={isReassignOpen} onClose={isReassigning ? undefined : onReassignClose} size="md" isCentered closeOnOverlayClick={!isReassigning}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Reassign Deal</ModalHeader>
          {!isReassigning && <ModalCloseButton />}
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Alert status="info" size="sm">
                <AlertIcon />
                <Text fontSize="sm">
                  Currently assigned to <strong>{ownerName}</strong>
                </Text>
              </Alert>
              <FormControl>
                <FormLabel fontSize="sm">Reassign to</FormLabel>
                <ChakraSelect
                  placeholder="Select loan officer"
                  value={reassignUserId}
                  onChange={(e) => setReassignUserId(e.target.value)}
                  isDisabled={isReassigning}
                >
                  {(() => {
                    const { dtcUsers, otherUsers } = groupCRMUsersByDTC(crmUsers);
                    return (
                      <>
                        {dtcUsers.length > 0 && (
                          <optgroup label="DTC Loan Officers">
                            {dtcUsers.map((user) => (
                              <option key={user.uid} value={user.uid}>{user.name}</option>
                            ))}
                          </optgroup>
                        )}
                        <optgroup label={dtcUsers.length > 0 ? 'Other Users' : 'Users'}>
                          {otherUsers.map((user) => (
                            <option key={user.uid} value={user.uid}>{user.name}</option>
                          ))}
                        </optgroup>
                      </>
                    );
                  })()}
                </ChakraSelect>
              </FormControl>
              <FormControl>
                <Checkbox
                  isChecked={notifyNewOwner}
                  onChange={(e) => setNotifyNewOwner(e.target.checked)}
                  isDisabled={isReassigning || !reassignUserId || reassignUserId === currentUser.uid}
                >
                  <Text fontSize="sm">Notify new owner</Text>
                </Checkbox>
                <Text fontSize="xs" color={mutedTextColor} mt={1} pl={6}>
                  Sends an email and a text message to the new owner. Off by default.
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onReassignClose} isDisabled={isReassigning}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleReassignDeal}
              isLoading={isReassigning}
              isDisabled={!reassignUserId}
            >
              Reassign Deal
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      
      {isSubmissionOpen && (
        <SubmissionDrawer
          isOpen={isSubmissionOpen}
          onClose={onSubmissionClose}
          action="Add"
          fromCRM={true}
          prefilledData={{
            borrowerFirstName: contact.firstName || '',
            borrowerLastName: contact.lastName || '',
            borrowerEmail: contact.email || '',
            borrowerPhone: contact.phone || '',
            middleInitial: contact.middleInitial || deal.middleInitial || '',
            loanAmount: deal.amount || '',
            loanType: deal.product || '',
            downPayment: deal.downPayment || '',
            creditScore: deal.creditScore || '',
            propertyAddress: deal.propertyAddress,
            propertyType: deal.propertyType || '',
            occupancyType: deal.occupancyType || 'primary_residence',
            purchaseOrRefi: deal.purchaseOrRefi || '',
            loanPurpose: deal.loanPurpose || '',
            estimatedClosingDate: deal.estimatedClosingDate,
            appDate: deal.appDate,
            buyerAgent: deal.buyerAgent,
            sellerAgent: deal.sellerAgent,
            source: deal.source,
            crmDealId: dealId,
            crmNotes: buildCRMNotesSummary(activities),
          }}
        />
      )}

      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose}>
        <AlertDialogOverlay><AlertDialogContent>
          <AlertDialogHeader>Delete Lead</AlertDialogHeader>
          <AlertDialogBody>
            <VStack align="stretch" spacing={3}>
              <Text>Are you sure you want to permanently delete this lead?</Text>
              <Box p={3} bg="red.50" borderRadius="md">
                <Text fontWeight="bold" mb={1}>{`${contact.firstName} ${contact.lastName}`}</Text>
                <Text fontSize="sm" color="gray.600">{contact.email}</Text>
              </Box>
              <Text fontSize="sm" fontWeight="bold" color="red.600">This action cannot be undone.</Text>
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onDeleteClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDelete} ml={3}>Yes, Delete Lead</Button>
          </AlertDialogFooter>
        </AlertDialogContent></AlertDialogOverlay>
      </AlertDialog>

      {/* Mobile-only quick actions. On a phone the sidebar's Quick Actions card sits
          below the whole activity timeline, so these are surfaced here instead.
          Hidden at lg+, so the desktop layout is untouched.
          Uses tel: rather than Dialpad click-to-call, which is the right affordance
          on a phone and avoids a second DialpadCallButton mount.
          pl clears the AI chat bubble, which is fixed bottom-left at 20px with a
          50px box and a higher z-index. */}
      <HStack
        display={{ base: 'flex', lg: 'none' }}
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        zIndex={1000}
        bg={cardBG}
        borderTopWidth="1px"
        borderColor={borderColor}
        px={3}
        pt={2}
        pb="calc(0.5rem + env(safe-area-inset-bottom))"
        pl="78px"
        spacing={1}
        justify="space-around"
      >
        <IconButton
          aria-label="Call"
          icon={<FiPhone />}
          size="lg"
          variant="ghost"
          colorScheme="blue"
          as={Link}
          href={`tel:${contact.phone}`}
          isDisabled={!contact.phone || contact.dnc || contact.dncCall}
          pointerEvents={!contact.phone || contact.dnc || contact.dncCall ? 'none' : 'auto'}
        />
        <IconButton
          aria-label="Send text message"
          icon={<FiMessageSquare />}
          size="lg"
          variant="ghost"
          colorScheme="green"
          onClick={onSMSOpen}
          isDisabled={contact.dnc || contact.dncSms}
        />
        <IconButton
          aria-label="Send email"
          icon={<FiMail />}
          size="lg"
          variant="ghost"
          colorScheme="purple"
          onClick={onEmailOpen}
          isDisabled={contact.dnc || contact.dncEmail}
        />
        <IconButton
          aria-label="Add task"
          icon={<FiCalendar />}
          size="lg"
          variant="ghost"
          colorScheme="purple"
          onClick={onUnifiedEventOpen}
        />
      </HStack>
    </Box>
  );
}